import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { QRCodeSVG } from "qrcode.react";
import "leaflet/dist/leaflet.css";
import { apiFetch, apiGet, apiSend } from "@/lib/api";
import { confirmDialog, toast } from "@/components/ui/feedback";
import { newBaseMap, projectFor, type BaseProvider } from "@/lib/amap";
import { pageWindow } from "@/lib/pagination";
import RentalSourceExportPanel from "./RentalSourceExportPanel";
// 扫码摄像头组件 — lazy 加载，html5-qrcode 只在用户点 📷 时下载。
const ScanCamera = lazy(() => import("@/components/ScanCamera"));

// 租借归还 — cinema-seat board of device slots (docs/06 §3).
// Slot code = 3-digit zero-padded number "001".."300"，每箱 7 套 = 43 箱。
// 运营输入只要 1..300 任意写法（"5"/"05"/"005"），后端 normalize。

// fmtSlot：把 "005" 这种 zero-padded 显示成 "5"，让运营看到的就是 1..300。
function fmtSlot(s: string): string {
  const n = parseInt(s, 10);
  return Number.isFinite(n) && String(n).length > 0 ? String(n) : s;
}

interface RentalSlot {
  slotCode: string;
  deviceId: string;
  deviceNo: string;
  battery: number;
  online: boolean;
  hasHardware: boolean;
  rentalStatus: string;
  orderId?: string;
  orderNo?: string;
  borrowedAt?: string;
  tripStarted?: boolean;
  tripEnded?: boolean;
  // Paired glasses (BLE-paired with this slot's phone).
  pairedId?: string;
  pairedNo?: string;
  pairedBattery?: number;
  pairedOnline?: boolean;
  pairedHasHardware?: boolean;
  pairedRentalStatus?: string;
  routeId?: string;
  routeName?: string;
  routeDirection?: "" | "up" | "down";
}
interface BindableGlass {
  id: string;
  deviceNo: string;
  model: string;
  battery: number;
  online: boolean;
}
interface RouteLite {
  id: string;
  name: string;
  shortName: string;
  status: string;
}
interface BindCandidate {
  id: string;
  deviceNo: string;
  deviceType: string;
  model: string;
  battery: number;
  online: boolean;
  pairedNo: string;
}
interface RentalBox { box: string; slots: RentalSlot[] }
interface Board { lendingOpen: boolean; boxes: RentalBox[]; stats: Record<string, number> }
interface UnreturnedRow {
  slotCode: string;
  deviceNo: string;
  orderNo: string;
  borrowedAt: string;
  borrowedAtFull: string;
  depositCents: number;
  rentCents: number;
}
interface ShipGroup {
  id: string;
  name: string;
  status: "active" | "dismissed";
  createdAt: string;
  dismissedAt?: string;
  // borrowed = 已激活(游客点了开始旅程),即"实际在用"的数量,和统计卡口径一致。
  borrowed: number;
  // pending = 已发放到分箱但游客还没点开始旅程的数量。不进统计,但要显示给运营。
  pending: number;
  returned: number;
  cleanupPending: number;
  // 船组状态条使用今日口径；borrowed 仍保留作批量归还等当前状态操作。
  todayInUse: number;
  todayReturned: number;
  routeId: string;
  routeName: string;
  routeDirection: "" | "up" | "down";
  boxes: number[];
}
interface BatchReturnResp {
  returned: number;
  ended?: number;
  cleanupOnly?: number;
  clearDataQueued?: number;
  alreadyQueued?: number;
  alreadyCleared?: number;
}
interface NarrationTrigger {
  id: string;
  name: string;
  sceneName: string;
  enabled: boolean;
  seq: number;
  updatedAt: string;
  /** 当前船组、当前游线/方向在上海当天成功启动的次数。 */
  todayFireCount: number;
  // 非空 = 由模板复制下来的实例(默认分发的触发器,不允许在船组下删除);
  // 空    = 本船组私建的触发器,可任意改/删。
  templateId?: string | null;
}

type SharedNarrationStatus = "eligible" | "waiting_receiver" | "expired" | "source_ineligible";
interface SharedNarrationStatusItem {
  eventId: number;
  sceneId: string;
  sceneName: string;
  seq: number;
  sourceDeviceNo: string;
  sourceSlotCode: string;
  startedAt: string;
  expiresAt: string;
  remainingSeconds: number;
  status: SharedNarrationStatus;
  statusReason: string;
  eligibleReceiverCount: number;
  reporterCount: number;
}
interface SharedNarrationStatusResp {
  shipGroupId: string;
  voyageId: string;
  route: { id: string; name: string; direction: "up" | "down" | "" };
  windowSeconds: number;
  serverNow: string;
  activeCount: number;
  items: SharedNarrationStatusItem[];
}


const LOW_BATTERY = 20;
const LEGACY_RENTAL_FLOW_ENABLED = false;
const PRODUCTION_BOX_COUNT = 43;
const TEST_BOX_NUMBER = PRODUCTION_BOX_COUNT + 1;

function rentalBoxLabel(box: string | number): string {
  const n = Number(box);
  return Number.isFinite(n) && n >= TEST_BOX_NUMBER ? "测试箱" : `第 ${box} 箱`;
}

const STATUS_META: Record<string, { label: string; cls: string }> = {
  available: { label: "可用", cls: "bg-emerald-500 hover:bg-emerald-600 text-white" },
  lowbat: { label: "低电", cls: "bg-sky-400 hover:bg-sky-500 text-white" },
  borrowed: { label: "在用", cls: "bg-amber-500 hover:bg-amber-600 text-white" },
  disabled: { label: "停用", cls: "bg-gray-300 hover:bg-gray-400 text-gray-600" },
  vacant: {
    label: "空置",
    cls: "bg-gray-50 text-gray-300 border border-dashed border-gray-300 hover:border-violet-400 hover:text-violet-400",
  },
};

// Display status: slots whose ledger row never saw real hardware are vacant
// (未放入设备); low-battery available units surface separately (L-7).
function displayStatus(s: RentalSlot): string {
  if (!s.hasHardware) return "vacant";
  if (s.tripStarted && !s.tripEnded) return "borrowed";
  if (s.rentalStatus === "disabled") return "disabled";
  if (s.battery < LOW_BATTERY) return "lowbat";
  return "available";
}

// 眼镜的显示状态：未配对 = vacant；其它情况跟手机的借出状态联动，
// 同时尊重眼镜自身的 disabled 标记和低电警告。
function glassDisplayStatus(s: RentalSlot): string {
  if (!s.pairedNo || !s.pairedHasHardware) return "vacant";
  if (s.tripStarted && !s.tripEnded) return "borrowed";
  const ownStatus = s.pairedRentalStatus || "available";
  if (ownStatus === "disabled") return "disabled";
  // 眼镜离线 → 按"停用"显示，提示运维处理（不再单独维护故障态）。
  if (s.pairedOnline === false) return "disabled";
  if ((s.pairedBattery ?? 0) < LOW_BATTERY) return "lowbat";
  return "available";
}

function moneyToCents(v: string, fallback: string): number {
  const n = parseFloat((v || fallback).replace(/,/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : Math.round(parseFloat(fallback) * 100);
}

function feeText(u: { orderNo: string; depositCents: number; rentCents?: number }): string {
  if (u.orderNo.startsWith("RCP-") || u.depositCents === 0) return "接待登记";
  const parts = [`押金 ¥${(u.depositCents / 100).toFixed(0)}`];
  if ((u.rentCents || 0) > 0) parts.push(`租金 ¥${((u.rentCents || 0) / 100).toFixed(0)}`);
  return parts.join(" · ");
}

// 使用统计:顶部卡片,按周期(今日/本周/本月/本季/本年)分组显示各船组使用次数。
// 数据源 GET /api/v1/rental/stats?period=…,后端 rental_stats.go 里已实现;
// "每次归还清数据后重启动算一次" 天然满足(每次开始使用都写新的 rental_orders 行)。
// 接待登记(order_no 前缀 RCP-)单列汇总,不计入正常船组统计。
type RentalStatsPeriod = "day" | "week" | "month" | "quarter" | "year";

const RENTAL_DETAIL_PAGE_SIZES = [20, 50, 100] as const;

interface ShipGroupStatRow {
  shipGroupId: string;
  name: string;
  status: string;
  routeId: string;
  routeName: string;
  routeDirection: string;
  count: number;
  firstAt: string;
  lastAt: string;
}

interface RentalStatsResp {
  period: RentalStatsPeriod;
  label: string;
  since: string;
  shipGroups: ShipGroupStatRow[];
  reception: { count: number; firstAt: string; lastAt: string };
  totalRental: number;
  totalReception: number;
}

interface RentalUsageDetail {
  id: string;
  rentalOrderId?: string;
  segmentId?: string;
  faceSwapOriginalAvailable?: boolean;
  deviceNo: string;
  slotCode: string;
  usedAt: string;
  routeName: string;
  routeDirection: string;
  orderNo: string;
  status: string;
  online: boolean;
  lastOnline: string;
  battery: number;
  startingBattery: number | null;
  totalOnlineSeconds: number | null;
  longestOnlineSeconds: number | null;
  shortestOnlineSeconds: number | null;
  hourlyBatteryDrain: number | null;
  glassesBattery: number | null;
  glassesBatteryMissCount: number;
  narratedCount: number;
  totalPoints: number;
  faceSwapStatus: "not_uploaded" | "processing" | "completed" | string;
  faceSwapFailureReason: string;
  faceSwapAlbumSessionId: string;
  faceSwapAlbumQRStatus: "not_available" | "not_generated" | "generated" | string;
  faceSwapAlbumStatus: "not_available" | "not_scanned" | "scanned" | "downloaded" | string;
  faceSwapAlbumQRCount: number;
  faceSwapAlbumScanCount: number;
  faceSwapAlbumDownloadCount: number;
  // Additive usage provenance fields are nullable on the API and omitted by legacy rows.
  appVersionName?: string | null;
  appVersionCode?: number | null;
  endedAt?: string | null;
  endSource?: string | null;
  clientEndSource?: string | null;
  clientEndOccurredAt?: string | null;
  clientEndReceivedAt?: string | null;
  clientEndAppVersionName?: string | null;
  clientEndAppVersionCode?: number | null;
  clientEndSceneId?: string | null;
  clientEndRoutePointKey?: string | null;
}

type RentalUsageEndSource = "route_completed" | "app_clear_data" | "admin_return" | "admin_reset" | "new_usage" | "unknown";

interface RentalUsageEndSourceMeta {
  key: RentalUsageEndSource;
  label: string;
  description: string;
}

const RENTAL_USAGE_END_SOURCE_META: Record<RentalUsageEndSource, RentalUsageEndSourceMeta> = {
  route_completed: {
    key: "route_completed",
    label: "末点完成",
    description: "最后点位的自动或同船讲解完成后，由客户端结束游线。",
  },
  app_clear_data: {
    key: "app_clear_data",
    label: "本机清理",
    description: "客户端主动清理本机本次使用数据。",
  },
  admin_return: {
    key: "admin_return",
    label: "后台归还",
    description: "后台执行归还操作后关闭本次使用。",
  },
  admin_reset: {
    key: "admin_reset",
    label: "后台重置",
    description: "后台重置仍在使用中的设备后关闭本次使用。",
  },
  new_usage: {
    key: "new_usage",
    label: "新使用替换",
    description: "新的使用开始时替换上一段未结束的使用。",
  },
  unknown: {
    key: "unknown",
    label: "未知",
    description: "旧记录、缺少来源或暂未识别到明确来源。",
  },
};

function rentalUsageEndSourceMeta(source: string | null | undefined): RentalUsageEndSourceMeta {
  if (typeof source === "string") {
    const normalized = source.trim() as RentalUsageEndSource;
    if (Object.prototype.hasOwnProperty.call(RENTAL_USAGE_END_SOURCE_META, normalized)) {
      return RENTAL_USAGE_END_SOURCE_META[normalized];
    }
  }
  return RENTAL_USAGE_END_SOURCE_META.unknown;
}

function rentalUsageVersionName(value: string | null | undefined): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function rentalUsageVersionCode(value: number | null | undefined): string | null {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? String(value) : null;
}

function rentalUsageHasClientEndEvidence(usage: RentalUsageDetail): boolean {
  return Boolean(
    rentalUsageEndSourceMeta(usage.clientEndSource).key !== "unknown" ||
      (typeof usage.clientEndSource === "string" && usage.clientEndSource.trim().length > 0) ||
      usage.clientEndOccurredAt ||
      usage.clientEndReceivedAt ||
      rentalUsageVersionName(usage.clientEndAppVersionName) ||
      rentalUsageVersionCode(usage.clientEndAppVersionCode) ||
      usage.clientEndSceneId ||
      usage.clientEndRoutePointKey,
  );
}

function rentalUsageContextLabel(usage: RentalUsageDetail): string {
  const slotLabel = usage.slotCode ? `${fmtSlot(usage.slotCode)} 号槽位` : "未匹配槽位";
  return `${slotLabel} · 设备 ${usage.deviceNo || "—"}`;
}

function RentalUsageVersionInfo({
  usage,
  onOpen,
}: {
  usage: RentalUsageDetail;
  onOpen: (usage: RentalUsageDetail, trigger: HTMLButtonElement) => void;
}) {
  const versionName = rentalUsageVersionName(usage.appVersionName);
  const versionCode = rentalUsageVersionCode(usage.appVersionCode);
  const shortLabel = versionName
    ? `App ${versionName}${versionCode ? ` · ${versionCode}` : ""}`
    : versionCode
      ? `App · ${versionCode}`
      : "App 版本未知";

  return (
    <button
      type="button"
      aria-haspopup="dialog"
      onClick={(event) => onOpen(usage, event.currentTarget)}
      title="点击查看完整 App 版本信息"
      aria-label={`查看${rentalUsageContextLabel(usage)}使用开始时 App 版本`}
      className="group mt-1 flex min-w-0 max-w-full items-center gap-1 rounded text-left text-[11px] text-gray-500 outline-none transition hover:text-teal-700 focus-visible:ring-2 focus-visible:ring-teal-500/30"
    >
      <span className="min-w-0 truncate">{shortLabel}</span>
      <i className="ri-information-line shrink-0 text-[10px] text-gray-400 group-hover:text-teal-600" aria-hidden="true" />
    </button>
  );
}

function RentalUsageEndInfo({
  usage,
  onOpen,
}: {
  usage: RentalUsageDetail;
  onOpen: (usage: RentalUsageDetail, trigger: HTMLButtonElement) => void;
}) {
  const inUse = usage.status === "in_use";
  const canonical = rentalUsageEndSourceMeta(usage.endSource);
  const hasClientEvidence = rentalUsageHasClientEndEvidence(usage);
  const client = rentalUsageEndSourceMeta(usage.clientEndSource);
  const sourceDiffers = hasClientEvidence && !inUse && canonical.key !== client.key;
  const sourceLabel = inUse ? "—" : canonical.key === "unknown" ? "来源未知" : canonical.label;
  const clientBadge = inUse
    ? hasClientEvidence ? "客户端证据" : null
    : sourceDiffers
      ? "双来源"
      : null;

  return (
    <button
      type="button"
      aria-haspopup="dialog"
      onClick={(event) => onOpen(usage, event.currentTarget)}
      title="点击查看完整结束信息"
      aria-label={`查看${rentalUsageContextLabel(usage)}${inUse ? "当前使用中的结束信息" : "结束信息"}`}
      className="group mt-0.5 flex min-w-0 max-w-full items-center gap-1 rounded text-left text-xs text-gray-500 outline-none transition hover:text-teal-700 focus-visible:ring-2 focus-visible:ring-teal-500/30"
    >
      <span className="min-w-0 truncate font-medium text-gray-600">{sourceLabel}</span>
      {clientBadge && (
        <span className={`min-w-0 max-w-[5rem] truncate rounded px-1 py-0.5 text-[10px] leading-4 ${sourceDiffers ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
          {clientBadge}
        </span>
      )}
      <i className="ri-information-line shrink-0 text-[10px] text-gray-400 group-hover:text-teal-600" aria-hidden="true" />
    </button>
  );
}

function RentalUsageInfoModal({ usage, onClose }: { usage: RentalUsageDetail; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const inUse = usage.status === "in_use";
  const canonical = rentalUsageEndSourceMeta(usage.endSource);
  const client = rentalUsageEndSourceMeta(usage.clientEndSource);
  const hasClientEvidence = rentalUsageHasClientEndEvidence(usage);
  const clientVersionName = rentalUsageVersionName(usage.clientEndAppVersionName);
  const clientVersionCode = rentalUsageVersionCode(usage.clientEndAppVersionCode);
  const slotLabel = usage.slotCode ? `${fmtSlot(usage.slotCode)} 号槽位` : "未匹配槽位";

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    closeButtonRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.getClientRects().length > 0);
      if (focusable.length === 0) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const activeIndex = focusable.indexOf(document.activeElement as HTMLElement);
      if (event.shiftKey && activeIndex <= 0) {
        event.preventDefault();
        event.stopPropagation();
        focusable[focusable.length - 1].focus({ preventScroll: true });
      } else if (!event.shiftKey && (activeIndex === -1 || activeIndex === focusable.length - 1)) {
        event.preventDefault();
        event.stopPropagation();
        focusable[0].focus({ preventScroll: true });
      }
    };

    dialog.addEventListener("keydown", handleKeyDown);
    return () => dialog.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center bg-black/40 p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rental-usage-info-title"
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4 sm:p-5">
          <div className="min-w-0 text-left">
            <h3 id="rental-usage-info-title" className="text-base font-bold text-gray-900">
              使用版本与结束信息
            </h3>
            <p className="mt-1 break-words text-xs text-gray-500">
              {slotLabel} · 设备 {usage.deviceNo || "—"} · 开始 {usage.usedAt || "未知"}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="关闭使用版本与结束信息"
            className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 outline-none transition hover:bg-gray-100 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-teal-500/30"
          >
            <i className="ri-close-line text-xl" aria-hidden="true" />
          </button>
        </div>

        <div
          role="region"
          aria-label="使用版本与结束信息内容"
          tabIndex={0}
          className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500/20 sm:px-5 sm:py-5"
        >
          <section>
            <h4 className="text-left text-sm font-semibold text-gray-800">使用开始时 App 版本</h4>
            <dl className="mt-2 space-y-2 text-left text-xs text-gray-600">
              <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                <dt className="text-gray-400">版本名</dt>
                <dd className="min-w-0 break-all text-gray-700">{rentalUsageVersionName(usage.appVersionName) ?? "未知（未上报）"}</dd>
              </div>
              <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                <dt className="text-gray-400">版本号</dt>
                <dd className="min-w-0 break-all text-gray-700">{rentalUsageVersionCode(usage.appVersionCode) ?? "未知（未上报）"}</dd>
              </div>
            </dl>
          </section>

          <section className="mt-5 border-t border-gray-200 pt-4">
            <h4 className="text-left text-sm font-semibold text-gray-800">服务端结束</h4>
            <dl className="mt-2 space-y-2 text-left text-xs text-gray-600">
              <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                <dt className="text-gray-400">来源</dt>
                <dd className="min-w-0 break-words text-gray-700">{inUse ? "使用中（暂无服务端结束）" : canonical.label}</dd>
              </div>
              <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                <dt className="text-gray-400">说明</dt>
                <dd className="min-w-0 break-words text-gray-700">{inUse ? "本次使用尚未记录服务端结束。" : canonical.description}</dd>
              </div>
              <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                <dt className="text-gray-400">服务端结束时间</dt>
                <dd className="min-w-0 break-all text-gray-700">{usage.endedAt || "未知（未记录）"}</dd>
              </div>
            </dl>
          </section>

          <section className="mt-5 border-t border-gray-200 pt-4">
            <h4 className="text-left text-sm font-semibold text-gray-800">客户端结束证据</h4>
            {hasClientEvidence ? (
              <dl className="mt-2 space-y-2 text-left text-xs text-gray-600">
                <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                  <dt className="text-gray-400">来源</dt>
                  <dd className="min-w-0 break-words text-gray-700">{client.label}</dd>
                </div>
                <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                  <dt className="text-gray-400">说明</dt>
                  <dd className="min-w-0 break-words text-gray-700">{client.description}</dd>
                </div>
                <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                  <dt className="text-gray-400">客户端发生时间</dt>
                  <dd className="min-w-0 break-all text-gray-700">{usage.clientEndOccurredAt || "未知（未记录）"}</dd>
                </div>
                <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                  <dt className="text-gray-400">服务端接收时间</dt>
                  <dd className="min-w-0 break-all text-gray-700">{usage.clientEndReceivedAt || "未知（未记录）"}</dd>
                </div>
                <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                  <dt className="text-gray-400">结束时 App</dt>
                  <dd className="min-w-0 break-all text-gray-700">
                    {clientVersionName || "未知（未上报）"}
                    {clientVersionCode ? ` · ${clientVersionCode}` : ""}
                  </dd>
                </div>
                {(usage.clientEndSceneId || usage.clientEndRoutePointKey) && (
                  <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3">
                    <dt className="text-gray-400">游线点位</dt>
                    <dd className="min-w-0 break-all text-gray-700">
                      {[usage.clientEndSceneId, usage.clientEndRoutePointKey].filter(Boolean).join(" · ") || "未知"}
                    </dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="mt-2 text-left text-xs text-gray-400">未记录客户端结束证据（旧记录或客户端未上报）。</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}


function formatLongestOnline(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 1) return "≤1秒";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return [hours > 0 ? `${hours}时` : "", minutes > 0 ? `${minutes}分` : "", remainingSeconds > 0 ? `${remainingSeconds}秒` : ""].join("");
}

const faceSwapStatusLabel: Record<string, string> = {
  completed: "已完成",
  processing: "处理中",
  not_uploaded_no_face_detected: "未上传：未检测到人脸",
  not_uploaded_selfie_read_failed: "未上传：照片读取失败",
  not_uploaded_upload_timeout: "未上传：上传超时",
  not_uploaded_upload_network_error: "未上传：网络异常",
  not_uploaded_upload_rejected: "未上传：服务拒绝",
  not_uploaded_upload_server_error: "未上传：服务异常",
  not_uploaded: "未上传",
};

const faceSwapAlbumStatusLabel: Record<string, string> = {
  not_available: "—",
  not_scanned: "未扫码",
  scanned: "已扫码",
  downloaded: "已下载",
};

function rentalUsageStatusMeta(status: string): { label: string; cls: string } {
  if (status === "in_use") return { label: "使用中", cls: "bg-emerald-50 text-emerald-700" };
  if (status === "ended") return { label: "已结束旅程", cls: "bg-amber-50 text-amber-700" };
  if (status === "returned") return { label: "已归还", cls: "bg-gray-100 text-gray-600" };
  return { label: status || "—", cls: "bg-gray-100 text-gray-600" };
}

function faceSwapFailureLabel(reason: string): string {
  const message = reason.match(/"message"\s*:\s*"([^"]+)"/)?.[1];
  return message || reason.replace(/^avatar generation failed:\s*/i, "").replace(/^FaceFusion job failed:\s*/i, "") || "换脸服务处理失败";
}

function matchUsageDetail(a: RentalUsageDetail, b: RentalUsageDetail): boolean {
  if (a.segmentId && b.segmentId) {
    return a.segmentId === b.segmentId && (a.rentalOrderId || a.id) === (b.rentalOrderId || b.id);
  }
  if (!a.segmentId && !b.segmentId) {
    return a.id === b.id;
  }
  return false;
}

function rentalUsageNarrationUrl(usage: RentalUsageDetail): string {
  const orderId = usage.rentalOrderId || usage.id;
  const query = usage.segmentId ? `?segmentId=${encodeURIComponent(usage.segmentId)}` : "";
  return `/rental/stats/details/${encodeURIComponent(orderId)}/narration${query}`;
}

function rentalUsageTrackUrl(usage: RentalUsageDetail): string {
  const orderId = usage.rentalOrderId || usage.id;
  const query = usage.segmentId ? `?segmentId=${encodeURIComponent(usage.segmentId)}` : "";
  return `/rental/stats/details/${encodeURIComponent(orderId)}/track${query}`;
}

function rentalUsageOriginalUrl(
  usage: RentalUsageDetail,
  period: RentalStatsPeriod,
  disposition: "inline" | "attachment" = "inline",
): string {
  const orderId = usage.rentalOrderId || usage.id;
  const params = new URLSearchParams();
  if (usage.segmentId) {
    params.set("segmentId", usage.segmentId);
  }
  params.set("disposition", disposition);
  if (period === "day") {
    params.set("period", "day");
  }
  return `/rental/stats/details/${encodeURIComponent(orderId)}/face-swap-original?${params.toString()}`;
}

function parseContentDispositionFilename(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const matchRfc5987 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (matchRfc5987 && matchRfc5987[1]) {
    try {
      return decodeURIComponent(matchRfc5987[1]);
    } catch {
      // ignore
    }
  }
  const match = header.match(/filename="?([^";]+)"?/i);
  if (match && match[1]) {
    return match[1].trim();
  }
  return fallback;
}

async function downloadOriginalFile(
  usage: RentalUsageDetail,
  period: RentalStatsPeriod,
  signal: AbortSignal,
): Promise<void> {
  if (period !== "day") {
    throw new Error("仅今日可下载原图");
  }
  if (signal.aborted) {
    throw new DOMException("原图下载已取消", "AbortError");
  }
  const url = rentalUsageOriginalUrl(usage, period, "attachment");
  const res = await apiFetch(url, { cache: "no-store", signal });
  if (!res.ok) {
    throw new Error(`下载原图失败 (${res.status})`);
  }
  const blob = await res.blob();
  if (signal.aborted) {
    throw new DOMException("原图下载已取消", "AbortError");
  }
  const filename = parseContentDispositionFilename(
    res.headers.get("Content-Disposition"),
    `${usage.deviceNo || "faceswap"}-original.jpg`
  );
  const blobUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 1000);
  }
}

interface RentalStatsDetailsResp {
  period: RentalStatsPeriod;
  label: string;
  since: string;
  shipGroupId: string;
  page: number;
  pageSize: number;
  total: number;
  details: RentalUsageDetail[];
}

interface RentalNarrationPoint {
  sceneId: string;
  name: string;
  seq: number;
  played: boolean;
  playedAt: string;
  playSources: string[];
}

interface RentalNarrationDetailsResp {
  orderId: string;
  points: RentalNarrationPoint[];
}

interface RentalUsageTrackPoint {
  at: string;
  lat: number;
  lng: number;
  gpsStatus: "healthy" | "stale_position" | "poor_accuracy" | "unknown" | string;
  accuracy: number;
  posAgeSec: number;
}

interface RentalUsageTrackResp {
  orderId: string;
  points: RentalUsageTrackPoint[];
  truncated: boolean;
}

const RENTAL_STATS_PERIODS: { key: RentalStatsPeriod; label: string }[] = [
  { key: "day", label: "今日" },
  { key: "week", label: "本周" },
  { key: "month", label: "本月" },
  { key: "quarter", label: "本季" },
  { key: "year", label: "本年" },
];

const NARRATION_SOURCE_LABELS: Record<string, string> = {
  auto: "自动播放",
  manual: "手动播放",
  shared: "同船播放",
  backend: "后台触发器",
};

function RentalStatsCard({ includeTestBox }: { includeTestBox: boolean }) {
  const [period, setPeriod] = useState<RentalStatsPeriod>("day");
  const [data, setData] = useState<RentalStatsResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailGroup, setDetailGroup] = useState<ShipGroupStatRow | null>(null);
  const [details, setDetails] = useState<RentalUsageDetail[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(50);
  const [detailTotal, setDetailTotal] = useState(0);
  const detailPageSizes = RENTAL_DETAIL_PAGE_SIZES;
  const detailTotalPages = Math.max(1, Math.ceil(detailTotal / detailPageSize));
  const [detailJumpPage, setDetailJumpPage] = useState(String(detailPage));
  const [detailPagerNav, setDetailPagerNav] = useState<HTMLElement | null>(null);
  const [detailPagerWidth, setDetailPagerWidth] = useState(0);
  const adoptedDetailPageRef = useRef<number | null>(null);
  const detailPagerRef = useCallback((node: HTMLElement | null) => setDetailPagerNav(node), []);
  const [narrationUsage, setNarrationUsage] = useState<RentalUsageDetail | null>(null);
  const [narrationPoints, setNarrationPoints] = useState<RentalNarrationPoint[]>([]);
  const [narrationLoading, setNarrationLoading] = useState(false);
  const [narrationError, setNarrationError] = useState("");
  const [trackUsage, setTrackUsage] = useState<RentalUsageDetail | null>(null);
  const [trackPoints, setTrackPoints] = useState<RentalUsageTrackPoint[]>([]);
  const [trackTruncated, setTrackTruncated] = useState(false);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState("");
  const [albumQRUsage, setAlbumQRUsage] = useState<RentalUsageDetail | null>(null);
  const [originalUsage, setOriginalUsage] = useState<RentalUsageDetail | null>(null);
  const [infoUsage, setInfoUsage] = useState<RentalUsageDetail | null>(null);
  const infoReturnFocusRef = useRef<HTMLButtonElement | null>(null);
  const includeTestBoxForToday = includeTestBox && period === "day";
  const testBoxQuery = includeTestBoxForToday ? "&includeTestBox=true" : "";
  const shipGroups = data?.shipGroups ?? [];

  const openInfoUsage = useCallback((usage: RentalUsageDetail, trigger: HTMLButtonElement) => {
    infoReturnFocusRef.current = trigger;
    setInfoUsage(usage);
  }, []);

  const closeInfoUsage = useCallback(() => {
    const returnFocusTarget = infoReturnFocusRef.current;
    infoReturnFocusRef.current = null;
    setInfoUsage(null);
    if (returnFocusTarget?.isConnected) {
      window.setTimeout(() => {
        if (returnFocusTarget.isConnected) returnFocusTarget.focus({ preventScroll: true });
      }, 0);
    }
  }, []);

  useEffect(() => {
    setDetailJumpPage(String(detailPage));
  }, [detailPage]);

  useEffect(() => {
    if (!detailPagerNav) return;
    const observer = new ResizeObserver(([entry]) => {
      setDetailPagerWidth(entry.contentRect.width);
    });
    observer.observe(detailPagerNav);
    return () => observer.disconnect();
  }, [detailPagerNav]);

  const handleDetailJumpCommit = () => {
    const trimmed = detailJumpPage.trim();
    if (trimmed !== "" && !isNaN(Number(trimmed))) {
      const page = Math.min(detailTotalPages, Math.max(1, Math.floor(Number(trimmed))));
      setDetailPage(page);
      setDetailJumpPage(String(page));
    } else {
      setDetailJumpPage(String(detailPage));
    }
  };

  const resetDetailSelection = () => {
    setNarrationUsage(null);
    setNarrationPoints([]);
    setNarrationError("");
    setTrackUsage(null);
    setTrackPoints([]);
    setTrackTruncated(false);
    setTrackError("");
    setAlbumQRUsage(null);
    setOriginalUsage(null);
    setInfoUsage(null);
    infoReturnFocusRef.current = null;
  };
  useEffect(() => {
    if (period !== "day") setOriginalUsage(null);
  }, [period]);

  const openDetailGroup = (group: ShipGroupStatRow) => {
    adoptedDetailPageRef.current = null;
    setDetailPage(1);
    setDetailTotal(0);
    setDetails([]);
    setDetailsError("");
    resetDetailSelection();
    setDetailGroup(group);
  };

  const changeDetailPageSize = (pageSize: number) => {
    adoptedDetailPageRef.current = null;
    setDetailPageSize(pageSize);
    setDetailPage(1);
    setDetailTotal(0);
    setDetails([]);
    setDetailsError("");
    resetDetailSelection();
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet<RentalStatsResp>(`/rental/stats?period=${period}${testBoxQuery}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    // 每 30s 拉一次,和 board 拉取频率对齐,不给后端加大压力。
    const t = setInterval(() => {
      apiGet<RentalStatsResp>(`/rental/stats?period=${period}${testBoxQuery}`)
        .then((d) => { if (!cancelled) setData(d); })
        .catch(() => {});
    }, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [period, testBoxQuery]);

  useEffect(() => {
    if (!detailGroup?.shipGroupId) return;
    let inFlight = false;
    let cancelled = false;
    const controller = new AbortController();
    const params = new URLSearchParams({
      period,
      shipGroupId: detailGroup.shipGroupId,
      routeId: detailGroup.routeId,
      routeDirection: detailGroup.routeDirection,
      page: String(detailPage),
      pageSize: String(detailPageSize),
    });
    if (includeTestBoxForToday) params.set("includeTestBox", "true");
    const path = `/rental/stats/details?${params.toString()}`;
    const adoptedPage = adoptedDetailPageRef.current === detailPage;
    if (adoptedPage) adoptedDetailPageRef.current = null;

    const loadDetails = async (mode: "blocking" | "refresh") => {
      if (inFlight) return;
      if (mode === "blocking") {
        setDetailsLoading(true);
        setDetailsError("");
        setDetails([]);
      }
      inFlight = true;
      try {
        const response = await apiGet<RentalStatsDetailsResp>(path, controller.signal);
        if (cancelled) return;
        setDetails(response.details);
        setDetailTotal(response.total);
        setDetailsError("");
        setNarrationUsage((current) => current ? response.details.find((item) => matchUsageDetail(item, current)) ?? null : null);
        setTrackUsage((current) => current ? response.details.find((item) => matchUsageDetail(item, current)) ?? null : null);
        setAlbumQRUsage((current) => current ? response.details.find((item) => matchUsageDetail(item, current)) ?? null : null);
        setOriginalUsage((current) => current ? response.details.find((item) => matchUsageDetail(item, current)) ?? null : null);
        setInfoUsage((current) => current ? response.details.find((item) => matchUsageDetail(item, current)) ?? null : null);
        if (response.page !== detailPage) {
          adoptedDetailPageRef.current = response.page;
          setDetailPage(response.page);
        }
      } catch (error) {
        if (cancelled || (error instanceof DOMException && error.name === "AbortError")) return;
        if (mode === "blocking") setDetailsError("明细加载失败，请稍后重试");
      } finally {
        inFlight = false;
        if (!cancelled && mode === "blocking") setDetailsLoading(false);
      }
    };

    if (adoptedPage) {
      setDetailsLoading(false);
    } else {
      void loadDetails("blocking");
    }
    const timer = window.setInterval(() => { void loadDetails("refresh"); }, 10_000);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
    };
  }, [
    detailGroup?.shipGroupId,
    detailGroup?.routeId,
    detailGroup?.routeDirection,
    detailPage,
    detailPageSize,
    includeTestBoxForToday,
    period,
  ]);

  const narrationURL = narrationUsage ? rentalUsageNarrationUrl(narrationUsage) : "";
  const trackURL = trackUsage ? rentalUsageTrackUrl(trackUsage) : "";

  useEffect(() => {
    if (!narrationURL) return;
    let cancelled = false;
    const url = narrationURL;
    const loadNarration = (initial = false) => {
      if (initial) {
        setNarrationLoading(true);
        setNarrationError("");
        setNarrationPoints([]);
      }
      apiGet<RentalNarrationDetailsResp>(url)
        .then((d) => { if (!cancelled) setNarrationPoints(d.points); })
        .catch(() => { if (!cancelled) setNarrationError("点位播放详情加载失败，请稍后重试"); })
        .finally(() => { if (!cancelled && initial) setNarrationLoading(false); });
    };
    loadNarration(true);
    const timer = setInterval(() => loadNarration(), 10_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [narrationURL]);

  useEffect(() => {
    if (!trackURL) return;
    let cancelled = false;
    const url = trackURL;
    const loadTrack = (initial = false) => {
      if (initial) {
        setTrackLoading(true);
        setTrackError("");
        setTrackPoints([]);
        setTrackTruncated(false);
      }
      apiGet<RentalUsageTrackResp>(url)
        .then((d) => {
          if (cancelled) return;
          setTrackPoints(d.points);
          setTrackTruncated(d.truncated);
        })
        .catch(() => { if (!cancelled) setTrackError("轨迹加载失败，请稍后重试"); })
        .finally(() => { if (!cancelled && initial) setTrackLoading(false); });
    };
    loadTrack(true);
    const timer = setInterval(() => loadTrack(), 10_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [trackURL]);


  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
      {/* 周期切换 + 汇总 */}
      <div className="flex flex-wrap items-center gap-2">
        <i className="ri-bar-chart-2-line text-xl text-teal-600" />
        <div className="text-base font-semibold text-gray-900">使用统计</div>
        {data && (
          <span className="text-xs text-gray-500">从 {data.since} 起累计</span>
        )}
        {includeTestBoxForToday && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">运维模式：今日含测试箱</span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {RENTAL_STATS_PERIODS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setPeriod(key);
                resetDetailSelection();
                setDetailGroup(null);
              }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition ${
                period === key
                  ? "bg-teal-600 text-white border-teal-600"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* 汇总数 */}
      {data && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <div>
            <span className="text-gray-500">使用总数</span>
            <span className="ml-2 text-xl font-bold text-teal-600">{data.totalRental}</span>
          </div>
          <div>
            <span className="text-gray-500">接待登记</span>
            <span className="ml-2 text-xl font-bold text-amber-600">{data.totalReception}</span>
          </div>
        </div>
      )}

      {/* 表格 */}
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full text-sm min-w-[620px]">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-200">
              <th className="text-left font-medium py-2 pl-4 sm:pl-0">船组</th>
              <th className="text-left font-medium py-2">航线</th>
              <th className="text-right font-medium py-2">使用次数</th>
              <th className="text-right font-medium py-2">首次 → 最近</th>
              <th className="text-right font-medium py-2 pr-4 sm:pr-0">明细</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data && (
              <tr><td colSpan={5} className="py-3 text-center text-gray-400">加载中…</td></tr>
            )}
            {data && shipGroups.length === 0 && data.totalReception === 0 && (
              <tr><td colSpan={5} className="py-3 text-center text-gray-400">
                {data.label}还没有使用记录
              </td></tr>
            )}
            {shipGroups.map((g) => (
              <tr key={`${g.shipGroupId || `unassigned-${g.name}`}-${g.routeId}-${g.routeDirection}`} className="border-b border-gray-100">
                <td className="py-2 pl-4 sm:pl-0">
                  <span className="text-gray-900 font-medium">{g.name}</span>
                  {g.status && g.status !== "active" && (
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{g.status}</span>
                  )}
                </td>
                <td className="py-2 text-gray-600">
                  {g.routeName ? (
                    <>{g.routeName}{g.routeDirection === "up" ? "·上" : g.routeDirection === "down" ? "·下" : ""}</>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="py-2 text-right font-semibold text-teal-700">{g.count}</td>
                <td className="py-2 text-right text-xs text-gray-500 tabular-nums">
                  {g.firstAt}
                  {g.firstAt !== g.lastAt && ` → ${g.lastAt}`}
                </td>
                <td className="py-2 text-right pr-4 sm:pr-0">
                  {g.shipGroupId ? (
                    <button
                      type="button"
                      onClick={() => openDetailGroup(g)}
                      className="px-2.5 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100"
                    >
                      明细
                    </button>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>
              </tr>
            ))}
            {data && data.reception.count > 0 && (
              <tr className="bg-amber-50/40">
                <td className="py-2 pl-4 sm:pl-0">
                  <span className="text-amber-700 font-medium">接待登记</span>
                  <span className="ml-2 text-xs text-amber-600">(不计入使用)</span>
                </td>
                <td className="py-2 text-gray-400">—</td>
                <td className="py-2 text-right font-semibold text-amber-700">{data.reception.count}</td>
                <td className="py-2 text-right text-xs text-gray-500 tabular-nums">
                  {data.reception.firstAt}
                  {data.reception.firstAt !== data.reception.lastAt && ` → ${data.reception.lastAt}`}
                </td>
                <td className="py-2 pr-4 sm:pr-0" />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detailGroup && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 p-3 sm:p-4"
          onClick={() => {
            resetDetailSelection();
            setDetailGroup(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${detailGroup.name}使用明细`}
            className="flex max-h-[85vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap items-start justify-between gap-3 p-4 sm:p-5 border-b border-gray-100">
              <div className="min-w-0">
                <h3 className="text-base font-bold text-gray-900">
                  <i className="ri-file-list-3-line text-teal-600 mr-1.5" />
                  {detailGroup.name} · 使用明细
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  {data?.label ?? "当前"}周期内共 {detailTotal} 条已开始旅程记录 · <span className="text-gray-400">版本与来源可点击查看完整信息</span>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 ml-auto">
                <RentalSourceExportPanel
                  group={detailGroup}
                  period={period}
                  includeTestBox={includeTestBox}
                />
                <button
                  type="button"
                  onClick={() => {
                    resetDetailSelection();
                    setDetailGroup(null);
                  }}
                  aria-label="关闭使用明细"
                  className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4 sm:p-5">
              {detailsLoading ? (
                <div className="py-8 text-center text-sm text-gray-400">加载中…</div>
              ) : detailsError ? (
                <div className="py-8 text-center text-sm text-red-500">{detailsError}</div>
              ) : details.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-400">该周期没有使用记录</div>
              ) : (
                <>
                  <div className="min-h-0 flex-1 overflow-auto">
                  <table className="w-[2080px] min-w-[2080px] table-fixed text-sm">
                    <colgroup>
                      <col style={{ width: "128px" }} />
                      <col style={{ width: "72px" }} />
                      <col style={{ width: "160px" }} />
                      <col style={{ width: "96px" }} />
                      <col style={{ width: "144px" }} />
                      <col style={{ width: "72px" }} />
                      <col style={{ width: "64px" }} />
                      <col style={{ width: "80px" }} />
                      <col style={{ width: "144px" }} />
                      <col style={{ width: "104px" }} />
                      <col style={{ width: "104px" }} />
                      <col style={{ width: "104px" }} />
                      <col style={{ width: "88px" }} />
                      <col style={{ width: "128px" }} />
                      <col style={{ width: "96px" }} />
                      <col style={{ width: "72px" }} />
                      <col style={{ width: "96px" }} />
                      <col style={{ width: "128px" }} />
                      <col style={{ width: "112px" }} />
                      <col style={{ width: "88px" }} />
                    </colgroup>
                    <thead className="sticky top-0 z-20 bg-gray-50">
                      <tr className="border-b border-gray-200 text-xs text-gray-500">
                        <th className="whitespace-nowrap px-3 py-2 text-left font-medium">设备编号</th>
                        <th className="sticky left-0 top-0 z-30 whitespace-nowrap bg-gray-50 px-3 py-2 text-left font-medium shadow-[2px_0_3px_-3px_rgba(15,23,42,0.45)]">槽位</th>
                        <th className="whitespace-nowrap px-3 py-2 text-left font-medium">使用时间</th>
                        <th className="whitespace-nowrap px-3 py-2 text-left font-medium">游线名称</th>
                        <th className="whitespace-nowrap px-3 py-2 text-left font-medium">状态</th>
                        <th className="whitespace-nowrap px-3 py-2 text-center font-medium">在线状态</th>
                        <th className="whitespace-nowrap px-3 py-2 text-center font-medium">电量</th>
                        <th className="whitespace-nowrap px-3 py-2 text-center font-medium">初始电量</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right font-medium">最近在线</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right font-medium" title="本次使用内全部连续亮屏时段的总和；独立心跳按 30 秒计算">总亮屏</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right font-medium" title="心跳间隔不超过 90 秒的一段连续在线时长；独立心跳按 30 秒计算">最长亮屏</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right font-medium" title="单次独立心跳按 30 秒计算">最短亮屏</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right font-medium" title="使用段首末电量差，除以最后心跳与开始旅程的间隔">耗电/小时</th>
                        <th className="whitespace-nowrap px-3 py-2 text-center font-medium" title="手机已回传电量、但未回传眼镜电量的心跳次数">眼镜电量未回传</th>
                        <th className="whitespace-nowrap px-3 py-2 text-center font-medium">AI换脸</th>
                        <th className="whitespace-nowrap px-3 py-2 text-center font-medium">原图</th>
                        <th className="whitespace-nowrap px-3 py-2 text-center font-medium">相册二维码</th>
                        <th className="whitespace-nowrap px-3 py-2 text-center font-medium">扫码次数</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right font-medium">讲解点位</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right font-medium">轨迹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.map((detail) => (
                        <tr key={detail.segmentId ? `${detail.rentalOrderId || detail.orderNo || "order"}:${detail.segmentId}` : detail.id} className="group border-b border-gray-100 align-middle transition-colors hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-900">{detail.deviceNo}</td>
                          <td className="sticky left-0 z-10 bg-white px-3 py-2 text-left text-gray-600 shadow-[2px_0_3px_-3px_rgba(15,23,42,0.45)] group-hover:bg-gray-50">{fmtSlot(detail.slotCode)} 号</td>
                          <td className="px-3 py-2 text-left text-gray-600 tabular-nums">
                            <div className="min-w-0 max-w-full">
                              <div className="truncate" title={detail.usedAt}>{detail.usedAt}</div>
                              <RentalUsageVersionInfo usage={detail} onOpen={openInfoUsage} />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-left text-gray-600">
                            <span
                              className="block max-w-full truncate"
                              title={`${detail.routeName || "—"}${detail.routeDirection === "up" ? "·上" : detail.routeDirection === "down" ? "·下" : ""}`}
                            >
                              {detail.routeName || "—"}
                              {detail.routeDirection === "up" ? "·上" : detail.routeDirection === "down" ? "·下" : ""}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-left">
                            <div className="flex min-w-0 max-w-full flex-col items-start gap-0.5">
                              {(() => {
                                const meta = rentalUsageStatusMeta(detail.status);
                                return (
                                  <span className={`inline-block max-w-full truncate rounded px-1.5 py-0.5 text-xs ${meta.cls}`}>
                                    {meta.label}
                                  </span>
                                );
                              })()}
                              <RentalUsageEndInfo usage={detail} onOpen={openInfoUsage} />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${detail.online ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                              {detail.online ? "在线" : "离线"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums">
                            {detail.battery >= 0 ? (
                              <span className={`text-xs font-medium ${detail.battery < LOW_BATTERY ? "text-red-600" : detail.battery < 60 ? "text-amber-600" : "text-emerald-700"}`}>
                                {detail.battery}%
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums">
                            {detail.startingBattery !== null ? (
                              <span className={`text-xs font-medium ${detail.startingBattery < LOW_BATTERY ? "text-red-600" : detail.startingBattery < 60 ? "text-amber-600" : "text-emerald-700"}`}>
                                {detail.startingBattery}%
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-xs text-gray-500 tabular-nums">{detail.lastOnline || "—"}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-right text-xs text-gray-600 tabular-nums">{formatLongestOnline(detail.totalOnlineSeconds)}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-right text-xs text-gray-600 tabular-nums">{formatLongestOnline(detail.longestOnlineSeconds)}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-right text-xs text-gray-600 tabular-nums">{formatLongestOnline(detail.shortestOnlineSeconds)}</td>
                          <td className="px-3 py-2 text-right text-xs text-gray-600 tabular-nums">
                            {detail.hourlyBatteryDrain === null ? "—" : `${detail.hourlyBatteryDrain.toFixed(1)}%`}
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums">
                            <span className={detail.glassesBatteryMissCount > 0 ? "text-xs font-medium text-amber-700" : "text-xs text-gray-400"}>
                              {detail.glassesBattery ?? "—"}（{detail.glassesBatteryMissCount}次）
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {detail.faceSwapStatus === "completed" ? (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">已完成</span>
                            ) : detail.faceSwapStatus === "processing" ? (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">处理中</span>
                            ) : detail.faceSwapStatus === "failed" ? (
                              <span
                                className="block max-w-full truncate rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700"
                                title={detail.faceSwapFailureReason || "换脸服务处理失败"}
                              >
                                失败：{faceSwapFailureLabel(detail.faceSwapFailureReason)}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">{faceSwapStatusLabel[detail.faceSwapStatus] ?? "未上传"}</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-center">
                            {period !== "day" ? (
                              <span className="text-xs text-gray-400" title="仅今日可查看原图">仅今日</span>
                            ) : detail.faceSwapOriginalAvailable ? (
                              <button
                                type="button"
                                onClick={() => {
                                  if (period === "day") setOriginalUsage(detail);
                                }}
                                aria-label={`查看${rentalUsageContextLabel(detail)} AI换脸原图`}
                                className="rounded border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700 transition hover:bg-teal-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30"
                              >
                                查看
                              </button>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {detail.faceSwapAlbumSessionId ? (
                              <button
                                type="button"
                                onClick={() => setAlbumQRUsage(detail)}
                                aria-label={`查看${rentalUsageContextLabel(detail)}相册二维码详情`}
                                className="text-xs text-sky-700 outline-none hover:text-sky-900 hover:underline focus-visible:ring-2 focus-visible:ring-sky-500/30"
                              >
                                详情
                              </button>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {detail.faceSwapAlbumStatus === "downloaded" || detail.faceSwapAlbumStatus === "scanned" ? (
                              <span
                                className="inline-block max-w-full truncate rounded bg-sky-50 px-1.5 py-0.5 text-xs text-sky-700"
                                title={`${detail.faceSwapAlbumScanCount} 次扫码`}
                              >
                                已扫码（{detail.faceSwapAlbumScanCount} 次）
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">
                                {faceSwapAlbumStatusLabel[detail.faceSwapAlbumStatus] ?? "未记录"}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => setNarrationUsage(detail)}
                              aria-label={`查看${rentalUsageContextLabel(detail)}讲解点位详情`}
                              className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-1 text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30 ${
                                detail.totalPoints > 0
                                  ? "text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
                                  : "text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-100"
                              }`}
                            >
                              <i className="ri-volume-up-line" aria-hidden="true" />
                              {detail.narratedCount}/{detail.totalPoints}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => {
                                setNarrationUsage(null);
                                setTrackUsage(detail);
                              }}
                              aria-label={`查看${rentalUsageContextLabel(detail)}轨迹`}
                              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700 outline-none hover:bg-sky-100 focus-visible:ring-2 focus-visible:ring-sky-500/30"
                            >
                              <i className="ri-route-line" aria-hidden="true" />
                              轨迹
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                  <div className="flex flex-col gap-3 pt-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>共 {detailTotal} 条记录，第 {detailPage} / {detailTotalPages} 页</span>
                      <label className="flex items-center gap-1.5 whitespace-nowrap">
                        每页
                        <select
                          value={detailPageSize}
                          onChange={(event) => changeDetailPageSize(Number(event.target.value))}
                          disabled={detailsLoading}
                          className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="每页记录数"
                        >
                          {detailPageSizes.map((pageSize) => <option key={pageSize} value={pageSize}>{pageSize}</option>)}
                        </select>
                      </label>
                    </div>
                    <nav ref={detailPagerRef} aria-label="使用明细分页导航" className="flex-1 min-w-0">
                      <div className={`flex items-center gap-2 ${detailPagerWidth >= 480 ? "hidden" : ""}`}>
                        <button
                          type="button"
                          onClick={() => setDetailPage((page) => Math.max(1, page - 1))}
                          disabled={detailsLoading || detailPage === 1}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label="上一页"
                          title="上一页"
                        >
                          <i className="ri-arrow-left-s-line" />
                        </button>
                        <span className="text-xs text-gray-600 px-1 font-medium">{detailPage} / {detailTotalPages}</span>
                        <button
                          type="button"
                          onClick={() => setDetailPage((page) => Math.min(detailTotalPages, page + 1))}
                          disabled={detailsLoading || detailPage === detailTotalPages}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label="下一页"
                          title="下一页"
                        >
                          <i className="ri-arrow-right-s-line" />
                        </button>
                      </div>
                      <div className={`${detailPagerWidth >= 480 ? "flex" : "hidden"} items-center justify-end gap-1.5`}>
                        <button
                          type="button"
                          onClick={() => setDetailPage((page) => Math.max(1, page - 1))}
                          disabled={detailsLoading || detailPage === 1}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label="上一页"
                          title="上一页"
                        >
                          <i className="ri-arrow-left-s-line" />
                        </button>
                        {pageWindow(
                          detailPage,
                          detailTotalPages,
                          Math.max(5, Math.min(11, Math.floor((detailPagerWidth - 156) / 38))),
                        ).map((page, index) =>
                          page === "ellipsis" ? (
                            <span
                              key={`ellipsis-${index}`}
                              className="w-8 h-8 flex items-center justify-center text-gray-400 text-xs"
                              aria-hidden="true"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={page}
                              type="button"
                              onClick={() => setDetailPage(page)}
                              disabled={detailsLoading}
                              className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                detailPage === page ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-50"
                              }`}
                              aria-current={detailPage === page ? "page" : undefined}
                            >
                              {page}
                            </button>
                          ),
                        )}
                        <button
                          type="button"
                          onClick={() => setDetailPage((page) => Math.min(detailTotalPages, page + 1))}
                          disabled={detailsLoading || detailPage === detailTotalPages}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label="下一页"
                          title="下一页"
                        >
                          <i className="ri-arrow-right-s-line" />
                        </button>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 ml-2">
                          <label htmlFor="rental-detail-jump-page-input" className="whitespace-nowrap">跳至</label>
                          <input
                            id="rental-detail-jump-page-input"
                            type="text"
                            value={detailJumpPage}
                            onChange={(event) => setDetailJumpPage(event.target.value)}
                            onBlur={handleDetailJumpCommit}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                handleDetailJumpCommit();
                                event.currentTarget.blur();
                              }
                            }}
                            disabled={detailsLoading}
                            className="w-11 h-8 px-1 text-center bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="跳转页码"
                          />
                          <span className="whitespace-nowrap">页</span>
                        </div>
                      </div>
                    </nav>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {infoUsage && (
        <RentalUsageInfoModal
          usage={infoUsage}
          onClose={closeInfoUsage}
        />
      )}

      {narrationUsage && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/30 p-3 sm:p-4"
          onClick={() => setNarrationUsage(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${narrationUsage.deviceNo}点位播放详情`}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 p-4 sm:p-5 border-b border-gray-100">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  <i className="ri-map-pin-line text-teal-600 mr-1.5" />
                  {narrationUsage.deviceNo} · 点位播放详情
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  已讲解 <span className="font-semibold text-emerald-700">{narrationUsage.narratedCount}</span> / 共 {narrationUsage.totalPoints} 个点位；自动与手动播放同一点只计一次
                </p>
              </div>
              <button
                type="button"
                onClick={() => setNarrationUsage(null)}
                aria-label="关闭点位播放详情"
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="overflow-auto p-4 sm:p-5">
              {narrationLoading ? (
                <div className="py-8 text-center text-sm text-gray-400">加载中…</div>
              ) : narrationError ? (
                <div className="py-8 text-center text-sm text-red-500">{narrationError}</div>
              ) : narrationPoints.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-400">该设备使用记录未关联可统计的游线点位</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {narrationPoints.map((point) => (
                    <div
                      key={point.sceneId}
                      className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                        point.played
                          ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                          : "bg-gray-50 border-gray-200 text-gray-500"
                      }`}
                    >
                      <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${point.played ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-500"}`}>
                        {point.played ? <i className="ri-check-line text-base" /> : point.seq}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{point.name}</div>
                        <div className={`mt-0.5 text-[11px] ${point.played ? "text-emerald-700" : "text-gray-400"}`}>
                          {point.played
                            ? `${point.playSources.map((source) => NARRATION_SOURCE_LABELS[source] ?? source).join("、")} · ${point.playedAt}`
                            : "未讲解"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {trackUsage && (
        <RentalUsageTrackModal
          usage={trackUsage}
          points={trackPoints}
          loading={trackLoading}
          error={trackError}
          truncated={trackTruncated}
          onClose={() => setTrackUsage(null)}
        />
      )}

      {albumQRUsage && (
        <RentalUsageAlbumQRModal
          usage={albumQRUsage}
          onClose={() => setAlbumQRUsage(null)}
        />
      )}

      {originalUsage && period === "day" && (
        <RentalUsageOriginalModal
          usage={originalUsage}
          period={period}
          onClose={() => setOriginalUsage(null)}
        />
      )}
    </div>
  );
}

const GPS_STATUS_LABELS: Record<string, string> = {
  healthy: "定位正常",
  stale_position: "定位陈旧",
  poor_accuracy: "定位精度差",
  unknown: "定位状态未知",
};

function RentalUsageAlbumQRModal({ usage, onClose }: { usage: RentalUsageDetail; onClose: () => void }) {
  const albumURL = `https://sanxia.pgv.sh.cn/h5/gallery/session/${encodeURIComponent(usage.faceSwapAlbumSessionId)}`;
  const slotLabel = usage.slotCode ? `${fmtSlot(usage.slotCode)} 号槽位` : "未匹配槽位";

  const copyAlbumURL = async () => {
    try {
      await navigator.clipboard.writeText(albumURL);
      toast("相册链接已复制");
    } catch {
      toast("复制失败，请手动复制链接");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${slotLabel}相册二维码详情`}
        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-gray-900">相册二维码详情</h3>
            <p className="mt-1 text-xs text-gray-500">{slotLabel} · {usage.usedAt}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭相册二维码详情"
            className="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="mt-5 flex justify-center rounded-xl bg-white p-3 ring-1 ring-gray-200">
          <QRCodeSVG value={albumURL} size={224} level="M" includeMargin />
        </div>
        <p className="mt-3 text-center text-xs text-gray-500">扫码打开该次使用对应的 AI 换脸相册</p>

        <div className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 break-all">
          {albumURL}
        </div>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => window.open(albumURL, "_blank", "noopener,noreferrer")}
            className="flex-1 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700 hover:bg-sky-100"
          >
            打开相册
          </button>
          <button
            type="button"
            onClick={() => void copyAlbumURL()}
            className="flex-1 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
          >
            复制链接
          </button>
        </div>
      </div>
    </div>
  );
}
function RentalUsageOriginalModal({
  usage,
  period,
  onClose,
}: {
  usage: RentalUsageDetail;
  period: RentalStatsPeriod;
  onClose: () => void;
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const downloadAbortRef = useRef<AbortController | null>(null);
  const slotLabel = usage.slotCode ? `${fmtSlot(usage.slotCode)} 号槽位` : "未匹配槽位";
  const inlineOriginalURL = rentalUsageOriginalUrl(usage, period, "inline");

  useEffect(() => {
    if (period !== "day") {
      downloadAbortRef.current?.abort();
      downloadAbortRef.current = null;
    }
    return () => {
      downloadAbortRef.current?.abort();
      downloadAbortRef.current = null;
    };
  }, [period]);
  useEffect(() => {
    if (period !== "day") {
      setLoading(false);
      setError("仅今日可查看原图");
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    let currentObjectUrl: string | null = null;

    setLoading(true);
    setError("");
    setImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    const fetchOriginal = async () => {
      try {
        const res = await apiFetch(inlineOriginalURL, { cache: "no-store", signal: controller.signal });
        if (!res.ok) {
          throw new Error(`加载原图失败 (${res.status})`);
        }
        const blob = await res.blob();
        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        currentObjectUrl = objUrl;
        setImageUrl(objUrl);
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setError(err instanceof Error ? err.message : "原图加载失败，请稍后重试");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchOriginal();

    return () => {
      cancelled = true;
      controller.abort();
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
        currentObjectUrl = null;
      }
    };
  }, [inlineOriginalURL, period]);

  const handleDownload = async () => {
    if (period !== "day" || downloading) return;
    downloadAbortRef.current?.abort();
    const controller = new AbortController();
    downloadAbortRef.current = controller;
    setDownloading(true);
    try {
      await downloadOriginalFile(usage, period, controller.signal);
      if (controller.signal.aborted || downloadAbortRef.current !== controller) return;
      toast("原图下载开始");
    } catch (err) {
      if (controller.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      toast(err instanceof Error ? err.message : "下载原图失败", "error");
    } finally {
      if (downloadAbortRef.current === controller) {
        downloadAbortRef.current = null;
        setDownloading(false);
      }
    }
  };

  const closeModal = () => {
    downloadAbortRef.current?.abort();
    downloadAbortRef.current = null;
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      onClick={closeModal}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${slotLabel}AI换脸原图`}
        className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-1.5">
              <i className="ri-image-line text-teal-600" />
              AI 换脸原图
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              {slotLabel} · 设备 {usage.deviceNo || "—"} · {usage.usedAt}
            </p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            aria-label="关闭原图预览"
            className="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="mt-4 flex-1 overflow-auto flex flex-col items-center justify-center min-h-[260px] p-2 bg-gray-50 rounded-xl border border-gray-100">
          {loading ? (
            <div className="py-12 flex flex-col items-center gap-2 text-sm text-gray-500">
              <i className="ri-loader-4-line animate-spin text-2xl text-teal-600" />
              <span>原图加载中…</span>
            </div>
          ) : error ? (
            <div className="py-12 flex flex-col items-center gap-2 text-sm text-red-500 text-center px-4">
              <i className="ri-error-warning-line text-2xl" />
              <span>{error}</span>
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt="AI换脸原图"
              className="max-h-[60vh] max-w-full rounded-lg object-contain shadow-sm"
            />
          ) : (
            <div className="py-12 text-sm text-gray-400">暂无原图</div>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => void handleDownload()}
            disabled={period !== "day" || loading || !!error || downloading}
            title={period === "day" ? undefined : "仅今日可查看原图"}
            className="flex-1 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-1.5"
          >
            <i className="ri-download-line text-base" />
            {downloading ? "下载中…" : "下载原图"}
          </button>
          <button
            type="button"
            onClick={closeModal}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}

function RentalUsageTrackModal({
  usage,
  points,
  loading,
  error,
  truncated,
  onClose,
}: {
  usage: RentalUsageDetail;
  points: RentalUsageTrackPoint[];
  loading: boolean;
  error: string;
  truncated: boolean;
  onClose: () => void;
}) {
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.Layer[]>([]);
  const [provider, setProvider] = useState<BaseProvider>("tdt");
  const project = useMemo(() => projectFor(provider), [provider]);
  const healthyCount = points.filter((point) => point.gpsStatus === "healthy").length;

  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    const map = newBaseMap(mapDivRef.current, provider, () => setProvider("amap"));
    map.setView([30.82, 111.0], 9);
    mapRef.current = map;
    requestAnimationFrame(() => map.invalidateSize());
    return () => {
      map.remove();
      mapRef.current = null;
      layersRef.current = [];
    };
  }, [provider, points.length]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || points.length === 0) return;
    layersRef.current.forEach((layer) => layer.remove());
    layersRef.current = [];

    const coords = points.map((point) => project(point.lat, point.lng));
    if (coords.length > 1) {
      layersRef.current.push(L.polyline(coords, {
        color: "#64748b", weight: 2, opacity: 0.65,
      }).addTo(map));
    }
    points.forEach((point, index) => {
      const healthy = point.gpsStatus === "healthy";
      const color = healthy ? "#2563eb" : "#eab308";
      const accuracy = point.accuracy >= 0 ? `${Math.round(point.accuracy)}m` : "未知";
      const age = point.posAgeSec >= 0 ? `${point.posAgeSec} 秒` : "未知";
      const edge = index === 0 || index === points.length - 1;
      const positionLabel = index === 0 ? "开始点" : index === points.length - 1 ? "结束点" : "定位点";
      const marker = L.circleMarker(project(point.lat, point.lng), {
        radius: edge ? 6 : 4,
        color,
        fillColor: color,
        fillOpacity: 0.9,
        weight: edge ? 3 : 1.5,
      }).addTo(map).bindTooltip(
        `${positionLabel}<br/>心跳时间：${point.at}<br/>GPS：${GPS_STATUS_LABELS[point.gpsStatus] ?? point.gpsStatus}<br/>精度：${accuracy}<br/>点位年龄：${age}<br/>${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`,
        { sticky: true },
      );
      layersRef.current.push(marker);
    });
    if (coords.length === 1) map.setView(coords[0], 16);
    else map.fitBounds(L.latLngBounds(coords), { padding: [36, 36], maxZoom: 16 });
  }, [points, project]);

  return (
    <div className="fixed inset-0 z-[65] flex items-end sm:items-center justify-center bg-black/30 p-3 sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${usage.deviceNo}使用轨迹`}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 p-4 sm:p-5 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900"><i className="ri-route-line text-sky-600 mr-1.5" />{usage.deviceNo} · 使用轨迹</h3>
            <p className="mt-1 text-xs text-gray-500">本次使用时间窗内的手机心跳定位点；悬停点位可查看时间与 GPS 状态。</p>
          </div>
          <button type="button" onClick={onClose} aria-label="关闭使用轨迹" className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="overflow-auto p-4 sm:p-5">
          {loading ? (
            <div className="py-20 text-center text-sm text-gray-400">轨迹加载中…</div>
          ) : error ? (
            <div className="py-20 text-center text-sm text-red-500">{error}</div>
          ) : points.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">本次使用未收到有效定位心跳</div>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                <span><i className="inline-block w-2.5 h-2.5 rounded-full bg-blue-600 mr-1.5" />蓝色：定位正常 {healthyCount}</span>
                <span><i className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400 mr-1.5" />黄色：其他状态 {points.length - healthyCount}</span>
                <span>共 {points.length} 个定位点</span>
                {truncated && <span className="text-amber-700">为保障地图性能，仅显示前 2000 个点</span>}
              </div>
              <div ref={mapDivRef} className="h-[420px] rounded-xl border border-gray-200 overflow-hidden" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const SHARED_NARRATION_STATUS_META: Record<SharedNarrationStatus, { label: string; cls: string }> = {
  eligible: { label: "可同船播放", cls: "bg-emerald-100 text-emerald-800" },
  waiting_receiver: { label: "有效中·暂无接收", cls: "bg-amber-100 text-amber-800" },
  expired: { label: "补播窗口已结束", cls: "bg-gray-100 text-gray-600" },
  source_ineligible: { label: "来源已失效", cls: "bg-red-100 text-red-700" },
};

const SHARED_NARRATION_REASON_LABELS: Record<string, string> = {
  window_expired: "补播窗口已结束",
  source_trip_ended: "来源手机已结束旅程或不再在线",
  source_left_scope: "来源手机已换航次、换组或退出绑定箱子",
  no_eligible_receiver: "当前没有其它在用手机可接收",
};

function sharedNarrationTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function sharedNarrationRemaining(expiresAt: string, nowMs: number): number {
  const remainingMs = new Date(expiresAt).getTime() - nowMs;
  return Number.isFinite(remainingMs) ? Math.max(0, Math.ceil(remainingMs / 1000)) : 0;
}

function sharedNarrationDuration(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function ShipGroupSharedNarrationsModal({ group, onClose }: { group: ShipGroup; onClose: () => void }) {
  const [data, setData] = useState<SharedNarrationStatusResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [nowMs, setNowMs] = useState(Date.now());
  const requestRef = useRef<{ token: number; controller: AbortController } | null>(null);
  const requestTokenRef = useRef(0);
  const scopeKey = `${group.id}:${group.routeId}:${group.routeDirection}`;

  const reload = useCallback(async () => {
    if (requestRef.current) return;
    const controller = new AbortController();
    const token = ++requestTokenRef.current;
    requestRef.current = { token, controller };
    setLoading(true);
    setError("");
    try {
      const next = await apiGet<SharedNarrationStatusResp>(
        `/rental/ship-groups/${encodeURIComponent(group.id)}/shared-narrations?view=recent`,
        controller.signal,
      );
      if (requestRef.current?.token !== token) return;
      setData(next);
      setNowMs(Date.now());
    } catch (e) {
      if (controller.signal.aborted || requestRef.current?.token !== token) return;
      setData(null);
      setError(e instanceof Error ? e.message : "同船播放状态加载失败");
    } finally {
      if (requestRef.current?.token === token) {
        requestRef.current = null;
        setLoading(false);
      }
    }
  }, [group.id, scopeKey]);

  useEffect(() => {
    setLoading(true);
    setError("");
    setData(null);
    void reload();
    const poll = setInterval(() => { void reload(); }, 10_000);
    const clock = setInterval(() => setNowMs(Date.now()), 1_000);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
      requestTokenRef.current += 1;
      requestRef.current?.controller.abort();
      requestRef.current = null;
    };
  }, [reload]);

  const currentItems = (data?.items ?? []).filter((item) => sharedNarrationRemaining(item.expiresAt, nowMs) > 0);
  const expiredItems = (data?.items ?? []).filter((item) => sharedNarrationRemaining(item.expiresAt, nowMs) === 0);
  const renderItem = (item: SharedNarrationStatusItem) => {
    const remaining = sharedNarrationRemaining(item.expiresAt, nowMs);
    const displayStatus: SharedNarrationStatus = remaining === 0 ? "expired" : item.status;
    const meta = SHARED_NARRATION_STATUS_META[displayStatus];
    const reason = displayStatus === "expired"
      ? SHARED_NARRATION_REASON_LABELS.window_expired
      : SHARED_NARRATION_REASON_LABELS[item.statusReason] || "";
    return (
      <div key={item.eventId} className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4 space-y-2">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center text-xs font-bold">{item.seq || "—"}</span>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-900 truncate">{item.sceneName}</div>
            <div className="mt-0.5 text-xs text-gray-500">最近上报：{item.sourceDeviceNo || "未知设备"}{item.sourceSlotCode ? ` · ${fmtSlot(item.sourceSlotCode)} 号` : ""}</div>
          </div>
          <span className={`shrink-0 text-xs px-2 py-1 rounded-full ${meta.cls}`}>{meta.label}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
          <div className="rounded-lg bg-gray-50 px-2.5 py-2"><div className="text-gray-400">最近上报</div><div className="mt-0.5 font-medium text-gray-700 tabular-nums">{sharedNarrationTime(item.startedAt)}</div></div>
          <div className="rounded-lg bg-gray-50 px-2.5 py-2"><div className="text-gray-400">到期时间</div><div className="mt-0.5 font-medium text-gray-700 tabular-nums">{sharedNarrationTime(item.expiresAt)}</div></div>
          <div className="rounded-lg bg-gray-50 px-2.5 py-2"><div className="text-gray-400">有效状态</div><div className={`mt-0.5 font-medium tabular-nums ${remaining > 0 ? "text-emerald-700" : "text-gray-500"}`}>{remaining > 0 ? `剩余 ${sharedNarrationDuration(remaining)}` : "补播窗口已结束"}</div></div>
          <div className="rounded-lg bg-gray-50 px-2.5 py-2"><div className="text-gray-400">今日上报设备</div><div className="mt-0.5 font-medium text-gray-700">{item.reporterCount} 台</div></div>
          <div className="rounded-lg bg-gray-50 px-2.5 py-2"><div className="text-gray-400">当前可接收</div><div className="mt-0.5 font-medium text-gray-700">{item.eligibleReceiverCount} 台</div></div>
        </div>
        {reason && <div className="text-xs text-gray-500"><i className="ri-information-line mr-1" />{reason}</div>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/30 p-3 sm:p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={`${group.name}今日同船自动触发记录`} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 p-4 sm:p-5 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900"><i className="ri-volume-up-line text-teal-600 mr-1.5" />{group.name} · 今日同船自动触发记录</h3>
            <p className="mt-1 text-xs text-gray-500">上海当天、当前航次的自动触发记录；同一点位合并显示，全部点位均展示，不按条数截断；补播窗口按 {data?.windowSeconds ?? 60} 秒计算；“今日上报设备”为该点位当前航次在上海当天上报的手机数。</p>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => void reload()} disabled={loading} title="立即刷新" className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-700 hover:bg-teal-50 rounded-lg disabled:opacity-50"><i className={`ri-refresh-line text-lg ${loading ? "animate-spin" : ""}`} /></button>
            <button type="button" onClick={onClose} aria-label="关闭同船播放状态" className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><i className="ri-close-line text-xl" /></button>
          </div>
        </div>
        <div className="overflow-auto p-4 sm:p-5 space-y-4">
          {!data && !error ? <div className="py-12 text-center text-sm text-gray-400">加载今日同船自动触发记录…</div>
            : error ? <div className="py-12 text-center text-sm text-red-500">{error}</div>
              : <>
                <div className="rounded-xl border border-teal-100 bg-teal-50 px-3 py-2.5 text-xs text-teal-900 flex flex-wrap gap-x-4 gap-y-1">
                  <span><i className="ri-route-line mr-1" />{data?.route.name || "当前航线"} · {data?.route.direction === "up" ? "上行" : "下行"} · 上海当天当前航次</span>
                </div>
                <section className="space-y-2">
                  <div className="text-sm font-semibold text-gray-800">今日当前补播窗口</div>
                  {currentItems.length ? currentItems.map(renderItem) : <div className="rounded-xl border border-dashed border-gray-200 py-8 text-center text-sm text-gray-400">今日暂无可观察的同船自动触发事件</div>}
                </section>
                <section className="space-y-2">
                  <div className="text-sm font-semibold text-gray-700">今日已过补播窗口</div>
                  {expiredItems.length ? expiredItems.map(renderItem) : <div className="rounded-xl border border-dashed border-gray-200 py-8 text-center text-sm text-gray-400">今日暂无已过补播窗口的同船自动触发点位</div>}
                </section>
                <p className="text-[11px] text-gray-400">“当前可接收”表示符合条件的候选手机数量，不代表已经成功播放到这些手机。</p>
              </>}
        </div>
      </div>
    </div>
  );
}

function RentalPage() {
  const [board, setBoard] = useState<Board>({ lendingOpen: true, boxes: [], stats: {} });
  const [unreturned, setUnreturned] = useState<UnreturnedRow[]>([]);
  const [selected, setSelected] = useState<RentalSlot | null>(null);
  const [deposit, setDeposit] = useState("200");
  const [rent, setRent] = useState("0");

  // ---- 扫码工作流（新主流程） ----
  // 扫码枪先填 voucher，回车 / 失焦 → 后端先校验是否已经在借：
  //   命中 → 显示「确认归还」红按钮；
  //   未命中 → 要求输入设备号 + 「确认借出」绿按钮。
  // 与下方座位图独立，互不干扰；座位图仍用于运维。
  const [scanVoucher, setScanVoucher] = useState("");
  const [scanDeviceNo, setScanDeviceNo] = useState("");
  const [scanBusy, setScanBusy] = useState(false);
  const [showLoanList, setShowLoanList] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  // 运维模式: clicking any slot opens the device-binding dialog instead of
  // the borrow/return flow (装柜/换机/撤柜).
  const [opsMode, setOpsMode] = useState(false);
  const [bindSlot, setBindSlot] = useState<RentalSlot | null>(null);
  const [candidates, setCandidates] = useState<BindCandidate[]>([]);
  const [candQuery, setCandQuery] = useState("");
  const [candSel, setCandSel] = useState("");

  const openBindDialog = async (slot: RentalSlot) => {
    setBindSlot(slot);
    setCandSel("");
    setCandQuery("");
    try {
      setCandidates(await apiGet<BindCandidate[]>("/rental/bindable"));
    } catch {
      setCandidates([]);
    }
  };

  const onBind = async () => {
    if (!bindSlot || !candSel) {
      toast("请选择要绑定的设备", "info");
      return;
    }
    try {
      await apiSend("POST", "/rental/bind", { slotCode: bindSlot.slotCode, deviceId: candSel });
      toast(`${fmtSlot(bindSlot.slotCode)} 号 已绑定设备`, "success");
      setBindSlot(null);
      await reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "绑定失败", "error");
    }
  };

  const onUnbind = async () => {
    if (!bindSlot) return;
    if (!(await confirmDialog({
      title: "解除设备绑定",
      message: `解除 ${fmtSlot(bindSlot.slotCode)} 号 与 ${bindSlot.deviceNo} 的绑定？槽位将变为空置。`,
    }))) return;
    try {
      await apiSend("POST", "/rental/bind", { slotCode: bindSlot.slotCode, deviceId: "" });
      toast(`${fmtSlot(bindSlot.slotCode)} 号 已置空`, "success");
      setBindSlot(null);
      await reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "操作失败", "error");
    }
  };

  // Registered-but-unslotted devices (e.g. fresh hardware that just
  // heartbeated): surfaced as 待入柜, selectable for one-click slotting.
  const [unslotted, setUnslotted] = useState<BindCandidate[]>([]);
  const [incomingSel, setIncomingSel] = useState("");

  // 游线绑定相关
  const [routes, setRoutes] = useState<RouteLite[]>([]);
  const [routeBusy, setRouteBusy] = useState(false);
  const activeRoutes = useMemo(() => routes.filter((r) => r.status === "active"), [routes]);

  // 船组（临时按船次成组的设备池子）
  const [shipGroups, setShipGroups] = useState<ShipGroup[]>([]);
  const [activeGroupId, setActiveGroupIdState] = useState<string>(() => localStorage.getItem("rental.activeGroupId") || "");
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showEditGroupBoxes, setShowEditGroupBoxes] = useState(false);
  const [scopeBusy, setScopeBusy] = useState(false);
  const [nextRouteId, setNextRouteId] = useState("");
  const [nextDirection, setNextDirection] = useState<"up" | "down">("up");
  const [groupUnreturned, setGroupUnreturned] = useState<UnreturnedRow[]>([]);
  const [showGroupLoanList, setShowGroupLoanList] = useState(false);
  const [triggers, setTriggers] = useState<NarrationTrigger[]>([]);
  const [showManageTriggers, setShowManageTriggers] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSharedNarrations, setShowSharedNarrations] = useState(false);
  const [triggerBusy, setTriggerBusy] = useState<string>("");
  // 接待登记（VIP / 免单批量借出，无支付凭证）
  const [showReception, setShowReception] = useState(false);
  const setActiveGroupId = useCallback((id: string) => {
    setActiveGroupIdState(id);
    if (id) localStorage.setItem("rental.activeGroupId", id);
    else localStorage.removeItem("rental.activeGroupId");
  }, []);
  const activeGroup = useMemo(() => shipGroups.find((g) => g.id === activeGroupId), [shipGroups, activeGroupId]);

  useEffect(() => {
    if (activeGroup) {
      setNextRouteId(activeGroup.routeId);
      setNextDirection(activeGroup.routeDirection || "up");
    }
  }, [activeGroup]);

  const reload = useCallback(async () => {
    try {
      const groupQs = activeGroupId ? `?group=${encodeURIComponent(activeGroupId)}` : "";
      const [b, u, ug, c, r, gs, ts] = await Promise.all([
        apiGet<Board>("/rental/board"),
        apiGet<UnreturnedRow[]>("/rental/unreturned"),
        activeGroupId ? apiGet<UnreturnedRow[]>(`/rental/unreturned${groupQs}`) : Promise.resolve([]),
        apiGet<BindCandidate[]>("/rental/bindable"),
        apiGet<RouteLite[]>("/routes"),
        apiGet<ShipGroup[]>("/rental/ship-groups?status=active"),
        activeGroupId ? apiGet<NarrationTrigger[]>(`/narration-triggers?groupId=${encodeURIComponent(activeGroupId)}`) : Promise.resolve([] as NarrationTrigger[]),
      ]);
      setBoard(b);
      setUnreturned(u);
      setGroupUnreturned(ug);
      setUnslotted(c);
      setRoutes(r);
      // 已上线的历史船组还没有箱绑定记录，旧 API 响应里的 boxes 可能为 null。
      // 统一成空数组，避免管理端渲染 .length/.join 时白屏。
      const normalizedGroups = gs.map((group) => ({
        ...group,
        boxes: Array.isArray(group.boxes) ? group.boxes : [],
        cleanupPending: Number.isFinite(group.cleanupPending) ? group.cleanupPending : 0,
      }));
      setShipGroups(normalizedGroups);
      setTriggers(ts);
      // 已选船组若被撤销/丢失，回退到空
      if (activeGroupId && !normalizedGroups.some((x) => x.id === activeGroupId)) {
        setActiveGroupId("");
      }
    } catch { /* keep current */ }
  }, [activeGroupId, setActiveGroupId]);

  // 给定 scope 切换航线 + 方向，统计影响台数并二次确认。
  // routeId="" 表示解绑；非空时 direction 必为 "up"|"down"（UI 平铺成"航线·上行/下行"两条选项）。
  const bindRoute = async (
    scope: "all" | "box" | "slot",
    target: string,
    routeId: string,
    direction: "" | "up" | "down",
    contextLabel: string,
  ) => {
    const route = routes.find((r) => r.id === routeId);
    const dirLabel = direction === "up" ? "上行" : direction === "down" ? "下行" : "";
    const targetSlots = board.boxes.flatMap((b) =>
      b.slots.filter((s) => {
        if (scope === "all") return s.hasHardware;
        if (scope === "box") return s.hasHardware && b.box === target;
        return s.hasHardware && s.slotCode === target;
      }),
    );
    if (targetSlots.length === 0) {
      toast("该范围内没有可绑定的设备（需先入柜）", "info");
      return;
    }
    const changing = targetSlots.filter(
      (s) => (s.routeId ?? "") !== routeId || (s.routeDirection ?? "") !== direction,
    );
    if (changing.length === 0) {
      toast("当前范围内的设备已经是该航线 / 方向", "info");
      return;
    }
    const ok = await confirmDialog({
      title: routeId ? `切换到「${route?.name ?? "?"} · ${dirLabel}」？` : "解除航线绑定？",
      message:
        `${contextLabel} 共 ${targetSlots.length} 台设备，其中 ${changing.length} 台需要切换。\n` +
        (routeId
          ? `切换后这些设备会改加载航线「${route?.name}」（${route?.shortName}） · ${dirLabel}。${scope === "all" ? "已绑定箱子的船组也会同步切换航次，并取消旧航次尚未执行的播放命令。" : ""}设备下次同步配置后生效。`
          : `将清除这些设备的航线 + 方向绑定。`),
      confirmText: routeId ? "确认切换" : "解除绑定",
      danger: !routeId,
    });
    if (!ok) return;
    setRouteBusy(true);
    try {
      const res = await apiSend<{ affected: number; shipGroupsAffected?: number }>(
        "POST",
        "/rental/route-bind",
        { scope, target, routeId, direction },
      );
      toast(
        routeId
          ? `已为 ${res.affected} 台设备绑定「${route?.name} · ${dirLabel}」${scope === "all" ? `，同步切换 ${res.shipGroupsAffected ?? 0} 个船组` : ""}`
          : `已解除 ${res.affected} 台设备的航线绑定`,
        "success",
      );
      await reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "切换失败", "error");
    } finally {
      setRouteBusy(false);
    }
  };

  useEffect(() => {
    reload();
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [reload]);

  // 选中船组后，座位图只保留它绑定箱子的手机/眼镜；未选船组才作为全局设备台账展示。
  const visibleBoxes = useMemo(() => {
    if (!activeGroup) return board.boxes;
    const boundBoxes = new Set(activeGroup.boxes.map(String));
    return board.boxes.filter((box) => boundBoxes.has(String(box.box)));
  }, [board.boxes, activeGroup]);

  const totalSlots = useMemo(
    () => visibleBoxes.reduce((n, b) => n + b.slots.length, 0),
    [visibleBoxes],
  );

  const visibleStats = useMemo(() => {
    if (!activeGroup) return board.stats;
    const stats: Record<string, number> = { available: 0, borrowed: 0, disabled: 0, vacant: 0 };
    for (const box of visibleBoxes) {
      for (const slot of box.slots) {
        const status = displayStatus(slot);
        // 低电设备仍是可用库存，和后端全局统计口径保持一致。
        stats[status === "lowbat" ? "available" : status]++;
      }
    }
    return stats;
  }, [activeGroup, board.stats, visibleBoxes]);

  const onInitSlots = async () => {
    if (!(await confirmDialog({
      title: "初始化编号",
      message: "为尚未编号的眼镜设备分配槽位号（A01 起，跳过 I/O）？",
    }))) return;
    const res = await apiSend<{ assigned: number }>("POST", "/rental/init-slots", {});
    toast(`已分配 ${res.assigned} 个编号`, "success");
    await reload();
  };

  const onToggleLending = async () => {
    const next = !board.lendingOpen;
    if (!next && !(await confirmDialog({
      title: "停止发放",
      message: "停止后，旧借还发放接口将被拒绝。确认停止？",
      danger: true,
    }))) return;
    await apiSend("POST", "/rental/lending", { open: next });
    toast(next ? "已恢复借出" : "已停止借出", "success");
    await reload();
  };

  const onReturn = async (slot: RentalSlot) => {
    if (!(await confirmDialog({
      title: "确认归还",
      message: `确认归还 ${slot.deviceNo || `${fmtSlot(slot.slotCode)} 号`}？该设备会立即结束旅程并收到清除数据指令。`,
      danger: true,
    }))) return;
    try {
      // 使用实物设备号精确归还当前点击的手机，避免输入槽位或误操作整箱。
      await apiSend("POST", "/rental/return", { deviceNo: slot.deviceNo, enqueueClearData: true });
      toast(`${slot.deviceNo || `${fmtSlot(slot.slotCode)} 号`} 已归还，清除数据指令已下发`, "success");
      setSelected(null);
      await reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "归还失败", "error");
    }
  };

  // 新建船组：航线 + 上/下行必填，扫码借出时按此校验
  const onCreateShipGroup = async (name: string, routeId: string, routeDirection: "up" | "down", boxes: number[]) => {
    const res = await apiSend<{ id: string }>("POST", "/rental/ship-groups",
      { name, routeId, routeDirection, boxes });
    setActiveGroupId(res.id);
    const r = routes.find((x) => x.id === routeId);
    toast(`船组「${name}」已创建，绑定 ${boxes.length} 个箱子`, "success");
    await reload();
  };

  const onEditGroupBoxes = () => {
    if (activeGroup) setShowEditGroupBoxes(true);
  };

  const onSaveGroupBoxes = async (boxes: number[]) => {
    if (!activeGroup) return;
    setScopeBusy(true);
    try {
      await apiSend("PUT", `/rental/ship-groups/${activeGroup.id}/boxes`, { boxes });
      toast("船组箱子绑定已更新", "success");
      setShowEditGroupBoxes(false);
      await reload();
    } catch (e) { toast(e instanceof Error ? e.message : "更新失败", "error"); }
    finally { setScopeBusy(false); }
  };

  const onSwitchGroupRoute = async (routeId: string, routeDirection: "up" | "down") => {
    if (!activeGroup || !routeId || (routeId === activeGroup.routeId && routeDirection === activeGroup.routeDirection)) return;
    setScopeBusy(true);
    try {
      const preview = await apiGet<{ canSwitch: boolean }>(`/rental/ship-groups/${activeGroup.id}/route-switch-preview`);
      if (!preview.canSwitch) {
        toast("无法切换：请先绑定至少一个箱子", "error");
        return;
      }
      const route = routes.find((item) => item.id === routeId);
      if (!(await confirmDialog({ title: "确认切换航线", message: `船组「${activeGroup.name}」已绑定 ${activeGroup.boxes.length} 个箱子，将切换至「${route?.name ?? routeId} · ${routeDirection === "up" ? "上行" : "下行"}」。在用设备会在下次同步后加载新航线；旧航次的待播命令会取消，历史统计保留在原航次。`, confirmText: "确认切换" }))) return;
      await apiSend("POST", `/rental/ship-groups/${activeGroup.id}/route-switch`, { routeId, routeDirection });
      toast("航线已切换，设备下次同步后加载新航线", "success");
      await reload();
    } catch (e) { toast(e instanceof Error ? e.message : "切换失败", "error"); }
    finally { setScopeBusy(false); }
  };
  // 给当前船组下发解说触发命令
  const onFireTrigger = async (t: NarrationTrigger) => {
    if (!activeGroup) { toast("请先选择船组", "info"); return; }
    if (activeGroup.borrowed === 0) { toast("本组当前没有在用手机", "info"); return; }
    if (!(await confirmDialog({
      title: `启动「${t.name}」`,
      message: `将向本船组「${activeGroup.name}」当前在用的 ${activeGroup.borrowed} 台手机下发播放命令。手机会按本地角色/语言/路线/方向匹配播放。`,
      confirmText: "确认启动",
    }))) return;
    setTriggerBusy(t.id);
    try {
      const res = await apiSend<{ sent: number; deviceCount: number; todayFireCount: number }>("POST", `/rental/ship-groups/${activeGroup.id}/trigger-narration`, { triggerId: t.id });
      setTriggers((current) => current.map((item) => item.id === t.id ? { ...item, todayFireCount: res.todayFireCount } : item));
      toast(`已向 ${res.deviceCount} 台在用手机下发（共 ${res.sent} 条命令）`, "success");
    } catch (e) { toast(e instanceof Error ? e.message : "下发失败", "error"); }
    finally { setTriggerBusy(""); }
  };

  // 触发器 CRUD —— 按当前 active 船组拉/写,船组之间互不可见。模板池由
  // 独立后台维护(本页不暴露),新建船组时后端已自动复制一份过来。
  const reloadTriggers = async () => {
    if (!activeGroupId) { setTriggers([]); return; }
    try {
      setTriggers(await apiGet<NarrationTrigger[]>(`/narration-triggers?groupId=${encodeURIComponent(activeGroupId)}`));
    } catch { /* keep */ }
  };

  // 船组批量发放/归还:后端 POST /rental/ship-groups/{id}/batch-issue|batch-return,
  // batch-issue 支持传 boxes:[N,N,...] 限定箱号(空 = 全发)。UI 上点批量发放
  // 弹一个箱组选择弹窗,展示每箱可发/在借/空位数,运营勾好再确认。
  const [batchIssueBusy, setBatchIssueBusy] = useState(false);
  const [batchReturnBusy, setBatchReturnBusy] = useState(false);
  const [showBatchIssueModal, setShowBatchIssueModal] = useState(false);
  interface BatchPreviewBox { box: number; available: number; borrowed: number; vacant: number }
  const [batchPreview, setBatchPreview] = useState<BatchPreviewBox[]>([]);
  const [batchSelectedBoxes, setBatchSelectedBoxes] = useState<Record<number, boolean>>({});
  const openBatchIssueModal = async () => {
    if (!activeGroup) return;
    setBatchSelectedBoxes({});
    try {
      const preview = await apiGet<BatchPreviewBox[]>(`/rental/ship-groups/${activeGroup.id}/batch-preview`);
      setBatchPreview(preview);
      // 默认全选:多数情况就是整船发,方便操作;运营需要减也就是取消勾。
      const preset: Record<number, boolean> = {};
      preview.forEach((b) => { if (b.available > 0) preset[b.box] = true; });
      setBatchSelectedBoxes(preset);
    } catch (e) {
      toast(e instanceof Error ? e.message : "预览失败", "error");
      return;
    }
    setShowBatchIssueModal(true);
  };
  const onConfirmBatchIssue = async () => {
    if (!activeGroup) return;
    const boxes = Object.keys(batchSelectedBoxes).filter((k) => batchSelectedBoxes[Number(k)]);
    if (boxes.length === 0) { toast("请至少选一个箱", "info"); return; }
    setBatchIssueBusy(true);
    try {
      const res = await apiSend<{ issued: number; skipped: number; skippedReasons?: string[] }>(
        "POST", `/rental/ship-groups/${activeGroup.id}/batch-issue`, { boxes }
      );
      const msg = res.skipped > 0
        ? `已发放 ${res.issued} 台,跳过 ${res.skipped} 台(${(res.skippedReasons || []).join("; ") || "详见后台日志"})`
        : `已批量发放 ${res.issued} 台`;
      toast(msg, res.issued > 0 ? "success" : "info");
      setShowBatchIssueModal(false);
      await reload();
    } catch (e) { toast(e instanceof Error ? e.message : "批量发放失败", "error"); }
    finally { setBatchIssueBusy(false); }
  };
  // 批量归还:当前借还流程已取消,这里作为"重置并清除手机数据"入口。
  // 后端同时处理两类手机:
  // 1) 行程中:结束 APPSTART 使用记录 + 下发 clear_data。
  // 2) 已结束但未清除:只补发 clear_data,不重复改归还状态。
  const [showBatchReturnModal, setShowBatchReturnModal] = useState(false);
  const openBatchReturnModal = async () => {
    if (!activeGroup) return;
    const inUseCount = activeGroup.borrowed || 0;
    const cleanupPendingCount = activeGroup.cleanupPending || 0;
    const totalCount = inUseCount + cleanupPendingCount;
    if (totalCount === 0) { toast("该船组当前没有在用或待清除手机", "info"); return; }
    if (!(await confirmDialog({
      title: "批量归还并清除数据",
      message: `船组「${activeGroup.name}」当前在用 ${inUseCount} 台，已结束但未清除 ${cleanupPendingCount} 台。确认后会结束在用手机，并向需要清除的手机下发「一键抹除所有数据」。手机收到后会停止讲解、清空游客数据并回到初始化状态。`,
      confirmText: "确认清除",
      danger: true,
    }))) {
      return;
    }
    setBatchReturnBusy(true);
    try {
      const res = await apiSend<BatchReturnResp>(
        "POST", `/rental/ship-groups/${activeGroup.id}/batch-return`, { resetInUse: true }
      );
      const ended = res.ended || 0;
      const cleanupOnly = res.cleanupOnly || 0;
      const queued = res.clearDataQueued || 0;
      const alreadyQueued = res.alreadyQueued || 0;
      toast(`已处理 ${res.returned} 台：结束 ${ended} 台，补清除 ${cleanupOnly} 台，新下发 ${queued} 条清除指令${alreadyQueued > 0 ? `，已有清除指令 ${alreadyQueued} 台` : ""}`, res.returned > 0 ? "success" : "info");
      await reload();
    } catch (e) { toast(e instanceof Error ? e.message : "批量归还失败", "error"); }
    finally { setBatchReturnBusy(false); }
  };
  const onConfirmBatchReturn = async () => {
    if (!activeGroup) return;
    const boxes = Object.keys(batchSelectedBoxes).filter((k) => batchSelectedBoxes[Number(k)]);
    if (boxes.length === 0) { toast("请至少选一个箱", "info"); return; }
    setBatchReturnBusy(true);
    try {
      const res = await apiSend<BatchReturnResp>(
        "POST", `/rental/ship-groups/${activeGroup.id}/batch-return`, { boxes }
      );
      toast(`已批量归还 ${res.returned} 台，新下发 ${res.clearDataQueued || res.returned} 条清除指令`, "success");
      setShowBatchReturnModal(false);
      await reload();
    } catch (e) { toast(e instanceof Error ? e.message : "批量归还失败", "error"); }
    finally { setBatchReturnBusy(false); }
  };
  // "本组已出借" 网格里的多选状态。key = slotCode(3 位零填充)。切换船组或
  // groupUnreturned 变了都要重置一次,防止残留旧勾选。
  const [selectedInGroupLoan, setSelectedInGroupLoan] = useState<Record<string, boolean>>({});
  useEffect(() => { setSelectedInGroupLoan({}); }, [activeGroupId]);
  useEffect(() => {
    // 有归还发生 → 清掉不再存在的槽位;保留还在借的选择,方便连续操作。
    setSelectedInGroupLoan((prev) => {
      const stillBorrowed = new Set(groupUnreturned.map((u) => u.slotCode));
      const next: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(prev)) if (v && stillBorrowed.has(k)) next[k] = true;
      return next;
    });
  }, [groupUnreturned]);
  const selectedGroupLoanCount = useMemo(
    () => Object.values(selectedInGroupLoan).filter(Boolean).length,
    [selectedInGroupLoan]
  );

  // 从"本组已出借"网格里精确归还所选槽位(不是按箱,单选/多选到 slot 都行)。
  const returnSelectedSlots = async (slots: string[]) => {
    if (!activeGroup) return;
    if (slots.length === 0) { toast("请先勾选要归还的设备", "info"); return; }
    if (!(await confirmDialog({
      title: "归还所选设备",
      message: `归还所选 ${slots.length} 台？清除数据指令会一起下发。`,
      danger: true,
    }))) return;
    setBatchReturnBusy(true);
    try {
      const res = await apiSend<{ returned: number }>(
        "POST", `/rental/ship-groups/${activeGroup.id}/batch-return`, { slots }
      );
      toast(`已归还 ${res.returned} 台`, "success");
      await reload();
    } catch (e) { toast(e instanceof Error ? e.message : "归还失败", "error"); }
    finally { setBatchReturnBusy(false); }
  };

  // 撤销当前船组（仅在该组无在用手机时允许）
  const onDismissActiveGroup = async () => {
    if (!activeGroup) return;
    if (activeGroup.borrowed > 0) { toast(`该船组还有 ${activeGroup.borrowed} 台手机在用，无法撤销`, "info"); return; }
    if (!(await confirmDialog({
      title: "撤销船组",
      message: `撤销船组「${activeGroup.name}」？撤销后不再用于触发器下发，历史统计仍保留。`,
      danger: true,
    }))) return;
    try {
      await apiSend("POST", `/rental/ship-groups/${activeGroup.id}/dismiss`, {});
      toast("船组已撤销", "success");
      setActiveGroupId("");
      await reload();
    } catch (e) { toast(e instanceof Error ? e.message : "撤销失败", "error"); }
  };

  // 扫码工作流：根据当前 voucher 在 unreturned 中是否命中决定走借/还分支。
  // 兼容输入支付单号、设备号（device_no）、槽位号（"1"/"01"/"001"）—— 接待
  // 订单没有真实支付凭证，运营只能凭设备号/槽位号归还。
  const scanMatch = useMemo(() => {
    const q = scanVoucher.trim();
    if (!q) return null;
    const slot3 = /^\d{1,3}$/.test(q) ? q.padStart(3, "0") : "";
    return (
      unreturned.find(
        (u) =>
          u.orderNo === q ||
          u.deviceNo === q ||
          (slot3 && u.slotCode === slot3),
      ) || null
    );
  }, [scanVoucher, unreturned]);

  const onScanBorrow = async () => {
    const v = scanVoucher.trim();
    const d = scanDeviceNo.trim();
    if (!v) { toast("请先扫支付凭证", "info"); return; }
    if (!d) { toast("请输入设备号", "info"); return; }
    if (!activeGroupId) { toast("请先选择或新建船组", "info"); return; }
    setScanBusy(true);
    try {
      const res = await apiSend<{ slotCode: string }>("POST", "/rental/borrow", {
        deviceNo: d,
        orderNo: v,
        depositCents: moneyToCents(deposit, "200"),
        rentCents: moneyToCents(rent, "0"),
        shipGroupId: activeGroupId,
      });
      toast(`已借出 ${fmtSlot(res.slotCode) || d} 号`, "success");
      setScanVoucher(""); setScanDeviceNo("");
      await reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "借出失败", "error");
    } finally { setScanBusy(false); }
  };

  const onScanReturn = async (match: { orderNo: string; depositCents: number; rentCents?: number; slotCode: string; deviceNo: string }) => {
    if (!match || !match.orderNo) return;
    if (!activeGroupId) { toast("请先选择船组（与借出时一致）", "info"); return; }
    const isReception = match.orderNo.startsWith("RCP-") || match.depositCents === 0;
    const msg = isReception
      ? `确认归还 ${fmtSlot(match.slotCode)} 号（${match.deviceNo || "接待"}）？该订单为接待登记，无押金。`
      : `确认归还该设备？押金将原路退回（支付单 ${match.orderNo}）。`;
    if (!(await confirmDialog({
      title: "确认归还",
      message: msg,
      danger: true,
    }))) return;
    setScanBusy(true);
    try {
      const res = await apiSend<{ slotCode: string }>("POST", "/rental/return", { orderNo: match.orderNo, shipGroupId: activeGroupId, enqueueClearData: true });
      toast(isReception ? `已归还 ${fmtSlot(res.slotCode || "")} 号` : `已归还 ${fmtSlot(res.slotCode || "")} 号，押金已退`, "success");
      setScanVoucher(""); setScanDeviceNo("");
      await reload();
    } catch {
      toast("未找到该订单的在借记录", "error");
    } finally { setScanBusy(false); }
  };

  const st = visibleStats;

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-5 pb-[max(env(safe-area-inset-bottom),12px)]">
      {/* Header + stats — 手机：标题/统计/操作各自换行；桌面：单行排齐。 */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <h1 className="text-xl font-bold text-gray-900">设备使用管理</h1>
        <span className="text-sm text-gray-500 leading-relaxed">
          在柜可用 <b className="text-emerald-600">{st.available || 0}</b> · 在用{" "}
          <b className="text-amber-600">{st.borrowed || 0}</b> · 停用{" "}
          <b className="text-gray-500">{st.disabled || 0}</b> · 空置{" "}
          <b className="text-gray-400">{st.vacant || 0}</b> · 共 {totalSlots} 槽位
        </span>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          {LEGACY_RENTAL_FLOW_ENABLED && (
            <button
              type="button"
              onClick={() => setShowLoanList(true)}
              className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-sm font-medium hover:bg-teal-100 active:scale-95 transition-transform"
            >
              <i className="ri-list-check-2 mr-1" />
              查看旧订单设备
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-teal-600 text-white text-xs font-semibold">
                {unreturned.length}
              </span>
            </button>
          )}
          {!activeGroup && board.boxes.length === 0 && (
            <button onClick={onInitSlots} className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900 active:scale-95 transition-transform">
              初始化编号
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowTemplates(true)}
            title="编辑模板会同步到所有船组（已建和未来新建）"
            className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-white border border-teal-200 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-50 active:scale-95 transition-transform"
          >
            <i className="ri-broadcast-line mr-1" />
            触发器模板
          </button>
          <button
            onClick={() => setOpsMode((v) => !v)}
            title="运维模式：点击槽位绑定/更换/撤出设备"
            className={`px-3 py-2 min-h-[44px] sm:min-h-0 rounded-lg text-sm font-medium active:scale-95 transition-transform ${
              opsMode ? "bg-violet-600 text-white hover:bg-violet-700" : "bg-violet-50 text-violet-700 hover:bg-violet-100"
            }`}
          >
            <i className="ri-tools-line mr-1" />
            运维模式{opsMode ? "·开" : ""}
          </button>
        </div>
      </div>

      {/* 使用统计卡:顶部显示各船组本周期使用次数 + 接待登记单列汇总。 */}
      <RentalStatsCard includeTestBox={opsMode} />

      {/* 船组（临时按船次成组的设备池子）—— 借出/归还都挂到当前选定船组。 */}
      <div className={`rounded-xl p-4 sm:p-5 shadow-sm space-y-3 border ${activeGroupId ? "bg-teal-50 border-teal-200" : "bg-amber-50 border-amber-200"}`}>
        <div className="flex flex-wrap items-center gap-2">
          <i className={`text-xl ${activeGroupId ? "ri-ship-line text-teal-600" : "ri-alert-line text-amber-600"}`} />
          <div className="text-base font-semibold text-gray-900">船组</div>
          {!activeGroupId && <span className="text-xs text-amber-700">未选船组，无法使用本船组触发器</span>}
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <select
              value={activeGroupId}
              onChange={(e) => setActiveGroupId(e.target.value)}
              className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-white border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              <option value="">— 请选择船组 —</option>
              {shipGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}（在用 {g.borrowed}{g.pending > 0 ? ` · 待激活 ${g.pending}` : ""}）</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowCreateGroup(true)}
              className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 active:scale-95 transition-transform"
            >
              <i className="ri-add-line mr-1" />新建船组
            </button>
            {activeGroup && (
              <>
                {LEGACY_RENTAL_FLOW_ENABLED && (
                  <>
                    <button
                      type="button"
                      onClick={openBatchIssueModal}
                      disabled={batchIssueBusy}
                      title="按船组批量发放:勾选箱号后一次性借出所选箱内所有可用设备"
                      className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 active:scale-95 transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <i className="ri-truck-line mr-1" />{batchIssueBusy ? "发放中…" : "批量发放"}
                    </button>
                    <button
                      type="button"
                      onClick={openBatchReturnModal}
                      disabled={batchReturnBusy || groupUnreturned.length === 0}
                      title="按船组批量归还:勾选箱号后归还并下清除数据指令"
                      className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 active:scale-95 transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <i className="ri-restart-line mr-1" />{batchReturnBusy ? "归还中…" : `批量归还 (${groupUnreturned.length})`}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowReception(true)}
                      title="无支付凭证的接待客人：输入手机号 + 一串设备号一次性批量借出"
                      className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 active:scale-95 transition-transform"
                    >
                      <i className="ri-user-star-line mr-1" />接待登记
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowGroupLoanList(true)}
                      className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-white border border-teal-200 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-50 active:scale-95 transition-transform"
                    >
                      <i className="ri-list-check-2 mr-1" />本组旧订单
                      <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-teal-600 text-white text-xs font-semibold">
                        {groupUnreturned.length}
                      </span>
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={openBatchReturnModal}
                  disabled={batchReturnBusy || ((activeGroup.borrowed || 0) + (activeGroup.cleanupPending || 0)) === 0}
                  title={((activeGroup.borrowed || 0) + (activeGroup.cleanupPending || 0)) === 0 ? "当前船组没有在用或待清除手机" : `结束在用 ${activeGroup.borrowed || 0} 台，补清除已结束未清除 ${activeGroup.cleanupPending || 0} 台`}
                  className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 active:scale-95 transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-restart-line mr-1" />{batchReturnBusy ? "清除中…" : `批量归还 (${(activeGroup.borrowed || 0) + (activeGroup.cleanupPending || 0)})`}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSharedNarrations(true)}
                  title="查看上海当天当前航次的同船自动触发记录与共享状态"
                  className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-white border border-violet-200 text-violet-700 rounded-lg text-sm font-medium hover:bg-violet-50 active:scale-95 transition-transform"
                >
                  <i className="ri-volume-up-line mr-1" />同船播放
                </button>
                <button
                  type="button"
                  onClick={onDismissActiveGroup}
                  disabled={activeGroup.borrowed > 0}
                  title={activeGroup.borrowed > 0 ? `还有 ${activeGroup.borrowed} 台手机在用` : "撤销当前船组"}
                  className="px-3 py-2 min-h-[44px] sm:min-h-0 bg-white border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-close-circle-line mr-1" />撤销当前船组
                </button>
              </>
            )}
          </div>
        </div>
        {activeGroup && (
          <div className="text-xs text-gray-600 flex items-center gap-x-3 flex-wrap">
            <span>当前船组：<b className="text-gray-900">{activeGroup.name}</b></span>
            {activeGroup.routeId ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 font-medium">
                <i className="ri-route-line" />
                {activeGroup.routeName || activeGroup.routeId}
                {activeGroup.routeDirection ? ` · ${activeGroup.routeDirection === "up" ? "上行" : "下行"}` : ""}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                <i className="ri-error-warning-line" />未绑定航线（旧船组）
              </span>
            )}
            <span title="仅统计上海时区今天开始旅程、当前仍在用的手机">今日在用 <b className="text-amber-600">{activeGroup.todayInUse}</b></span>
            {activeGroup.pending > 0 && (
              <span title="已发放到分箱但游客还没点开始旅程,尚未计入使用统计" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[11px]">
                <i className="ri-hourglass-line" />待激活 {activeGroup.pending}
              </span>
            )}
            <span title="仅统计上海时区今天完成归还的记录">今日已归还 <b className="text-emerald-600">{activeGroup.todayReturned}</b></span>
            <span className="text-gray-400">创建于 {activeGroup.createdAt}</span>
          </div>
        )}
        {activeGroup && (
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-teal-200/60 text-xs">
            <span className="text-gray-500">绑定箱子：</span>
            <span className="font-medium text-gray-800">{activeGroup.boxes.length ? `已绑定 ${activeGroup.boxes.length} 个箱子` : "未绑定"}</span>
            <button type="button" onClick={onEditGroupBoxes} disabled={scopeBusy} className="px-2.5 py-1.5 rounded-md border border-teal-200 bg-white text-teal-700 hover:bg-teal-50 disabled:opacity-50">
              <i className="ri-inbox-archive-line mr-1" />配置箱子
            </button>
            <span className="ml-auto text-gray-500">切换航线：</span>
            <select value={nextRouteId} onChange={(e) => void onSwitchGroupRoute(e.target.value, nextDirection)} disabled={scopeBusy} className="px-2 py-1.5 rounded-md border border-gray-200 bg-white max-w-40">
              {activeRoutes.map((route) => <option key={route.id} value={route.id}>{route.shortName || route.name}</option>)}
            </select>
            <select value={nextDirection} onChange={(e) => void onSwitchGroupRoute(nextRouteId, e.target.value as "up" | "down")} disabled={scopeBusy} className="px-2 py-1.5 rounded-md border border-gray-200 bg-white">
              <option value="up">上行</option><option value="down">下行</option>
            </select>
          </div>
        )}
        {activeGroup && (
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-teal-200/60">
            <span className="text-xs text-gray-500"><i className="ri-broadcast-line mr-1" />解说触发：</span>
            {triggers.filter((t) => t.enabled).map((t) => (
              <button
                key={t.id}
                onClick={() => onFireTrigger(t)}
                disabled={triggerBusy === t.id || activeGroup.borrowed === 0}
                title={`场景：${t.sceneName}（按手机端方向选 ·上 / ·下）；仅统计当前船组、当前游线/方向在上海当天的成功启动次数`}
                className="px-3 py-2 min-h-[44px] bg-white border border-teal-300 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-50 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-play-circle-line mr-1" />{triggerBusy === t.id ? "下发中…" : `启动「${t.name}」`}
                <span className="ml-1.5 text-xs font-semibold tabular-nums text-teal-600">今日 {t.todayFireCount ?? 0} 次</span>
              </button>
            ))}
            {triggers.filter((t) => t.enabled).length === 0 && (
              <span className="text-xs text-gray-400">还没有启用的触发器</span>
            )}
            <button
              onClick={() => setShowManageTriggers(true)}
              className="ml-auto text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
            >
              <i className="ri-settings-3-line mr-0.5" />本船组触发器
            </button>
          </div>
        )}
      </div>

      {showSharedNarrations && activeGroup && (
        <ShipGroupSharedNarrationsModal group={activeGroup} onClose={() => setShowSharedNarrations(false)} />
      )}

      {/* 扫码工作流 — 边缘场景保留(单台故障调换、审计对账等)。默认收起,
          点标题栏"手动扫码"展开。主流程走"批量发放/归还"。 */}
      {LEGACY_RENTAL_FLOW_ENABLED && (
      <details className={`bg-white rounded-xl border border-gray-200 shadow-sm ${!activeGroupId ? "opacity-60 pointer-events-none" : ""}`}>
        <summary className="cursor-pointer select-none px-4 sm:px-5 py-3 flex items-center gap-2 text-sm text-gray-600 hover:bg-gray-50">
          <i className="ri-qr-scan-2-line text-gray-500" />
          <span>手动扫码借出 / 归还</span>
          <span className="text-xs text-gray-400">(单台故障调换 · 审计对账用,主流程见上方"批量发放/归还")</span>
        </summary>
        <div className="p-4 sm:p-5 pt-0 space-y-3">
        <div className="flex items-center gap-2">
          <i className="ri-qr-scan-2-line text-teal-600 text-xl" />
          <div className="text-base font-semibold text-gray-900">扫码借出 / 归还</div>
          {scanMatch ? (
            <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              已借出 · 可归还
            </span>
          ) : scanVoucher.trim() ? (
            <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
              未借出 · 可借出
            </span>
          ) : null}
        </div>

        <div className="space-y-2">
          <label className="block text-xs text-gray-500">① 支付凭证（手机摄像头扫码 / 扫码枪 / 手动输入）</label>
          <div className="flex gap-2">
            <input
              autoFocus
              value={scanVoucher}
              onChange={(e) => setScanVoucher(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                if (scanMatch) {
                  onScanReturn(scanMatch);
                } else {
                  // 焦点跳到设备号输入框
                  const el = document.getElementById("scan-device-no") as HTMLInputElement | null;
                  el?.focus();
                }
              }}
              placeholder="扫支付凭证…回车继续"
              className="flex-1 min-w-0 px-3 py-3 min-h-[52px] border border-gray-300 rounded-lg text-base font-mono tracking-wide focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
            />
            <button
              type="button"
              onClick={() => setCameraOpen(true)}
              aria-label="打开摄像头扫码"
              className="shrink-0 min-h-[52px] w-[60px] flex items-center justify-center bg-teal-600 text-white rounded-lg hover:bg-teal-700 active:scale-95 transition-transform"
            >
              <i className="ri-camera-line text-2xl" />
            </button>
          </div>
        </div>

        {scanMatch ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2">
            <div className="text-sm text-amber-900">
              {scanMatch.orderNo.startsWith("RCP-") || scanMatch.depositCents === 0 ? "该设备 " : "该凭证对应 "}
              <b>设备 {fmtSlot(scanMatch.slotCode) || scanMatch.deviceNo} 号</b> · {scanMatch.borrowedAtFull} 开始使用
              {scanMatch.orderNo.startsWith("RCP-") || scanMatch.depositCents === 0
                ? " · 接待登记（无押金）"
                : ` · ${feeText(scanMatch)}`}
            </div>
            <button
              onClick={() => onScanReturn(scanMatch)}
              disabled={scanBusy}
              className="w-full px-4 py-3 min-h-[52px] bg-red-600 text-white rounded-lg text-base font-semibold hover:bg-red-700 disabled:opacity-50 active:scale-[0.98] transition-transform"
            >
              <i className="ri-arrow-go-back-line mr-1.5" />
              {scanMatch.orderNo.startsWith("RCP-") || scanMatch.depositCents === 0 ? "确认归还" : "确认归还 · 退押金"}
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <label className="block text-xs text-gray-500">② 设备号（手动输入待出借设备的编号）</label>
              <input
                id="scan-device-no"
                value={scanDeviceNo}
                onChange={(e) => setScanDeviceNo(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onScanBorrow()}
                placeholder="输入设备号 1-300，如 5、12、108"
                inputMode="numeric"
                className="w-full px-3 py-3 min-h-[52px] border border-gray-300 rounded-lg text-base font-mono tracking-wide focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                disabled={!scanVoucher.trim()}
              />
            </div>
            <button
              onClick={onScanBorrow}
              disabled={scanBusy || !scanVoucher.trim() || !scanDeviceNo.trim()}
              className="w-full px-4 py-3 min-h-[52px] bg-teal-600 text-white rounded-lg text-base font-semibold hover:bg-teal-700 disabled:opacity-40 active:scale-[0.98] transition-transform"
            >
              <i className="ri-check-line mr-1.5" />
              确认借出
            </button>
          </>
        )}

        {!board.lendingOpen && (
          <div className="text-xs text-red-600">
            <i className="ri-error-warning-line mr-1" />
            后端 lending_open 已关闭，借出会被拒绝，但归还不受影响。
          </div>
        )}
        </div>
      </details>
      )}

      {LEGACY_RENTAL_FLOW_ENABLED && !board.lendingOpen && (
        <div className="px-4 py-2.5 bg-red-50 text-red-700 rounded-lg text-sm">
          <i className="ri-error-warning-line mr-1" />
          已停止借出（行程末段）。归还操作不受影响。
        </div>
      )}

      {/* 全局航线绑定：一键把所有设备绑定到某条游线（含二次确认） */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <i className="ri-route-line text-teal-600 text-lg" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900">航线绑定（全局）</div>
          <div className="text-xs text-gray-500">
            把所有入柜设备一键切换到同一条游线；切换需二次确认。每个箱柜下也有独立切换。
          </div>
        </div>
        <div className="w-full min-w-0 sm:ml-auto sm:w-auto sm:max-w-md">
          <select
            disabled={routeBusy || activeRoutes.length === 0}
            defaultValue=""
            onChange={(e) => {
              const v = e.target.value;
              // 选完后立刻发起切换；这里不留状态，select 会回到默认占位
              e.currentTarget.value = "";
              if (v === "__unbind") { bindRoute("all", "", "", "", "全部设备"); return; }
              if (!v) return;
              const [rid, dir] = v.split("|") as [string, "up" | "down"];
              bindRoute("all", "", rid, dir, "全部设备");
            }}
            className="w-full max-w-full min-w-0 truncate px-3 py-2 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-sm disabled:opacity-50"
          >
            <option value="" disabled>
              {activeRoutes.length === 0 ? "暂无可用游线" : "选择航线 + 方向…"}
            </option>
            {activeRoutes.flatMap((r) => [
              <option key={`${r.id}|up`} value={`${r.id}|up`}>
                切换全部 → {r.name}（{r.shortName}）· 上行
              </option>,
              <option key={`${r.id}|down`} value={`${r.id}|down`}>
                切换全部 → {r.name}（{r.shortName}）· 下行
              </option>,
            ])}
            <option value="__unbind">解除全部设备的航线 + 方向绑定…</option>
          </select>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded ${k === "vacant" ? "bg-gray-50 border border-dashed border-gray-300" : m.cls.split(" ")[0]}`} />
            {m.label}
          </span>
        ))}
        <span className="text-gray-400">
          {opsMode
            ? "运维模式：点击任意槽位绑定 / 更换 / 撤出设备。"
            : "槽位角标 = 设备电量%。点击空置槽位可装入已选待入柜设备；其它设备操作请开启运维模式。"}
        </span>
      </div>

      {/* Incoming devices awaiting a slot — online only: an offline device
          isn't actionable for installation (offline ones stay reachable via
          the ops-mode dialog's full list). */}
      {!activeGroup && unslotted.filter((c) => c.online).length > 0 && (
        <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-inbox-unarchive-line text-amber-600" />
            <span className="text-sm font-semibold text-gray-900">待入柜设备 ({unslotted.filter((c) => c.online).length})</span>
            <span className="text-xs text-gray-500">
              一套 = 手机 + 已配对眼镜，以手机为入柜主体。{incomingSel ? "已选中，点击下方任意「空置」槽位即可装入整套。" : "点选手机后，再点空置槽位一步入柜。"}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {unslotted.filter((c) => c.online).map((c) => (
              <button
                key={c.id}
                onClick={() => setIncomingSel(incomingSel === c.id ? "" : c.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                  incomingSel === c.id
                    ? "border-amber-500 bg-amber-100 text-amber-900"
                    : "border-amber-200 bg-white text-gray-700 hover:border-amber-400"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${c.online ? "bg-emerald-500" : "bg-gray-300"}`} />
                <span className="font-mono font-medium">{c.deviceNo}</span>
                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px]">手机</span>
                {c.pairedNo ? (
                  <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-600 text-[10px]">
                    <i className="ri-glasses-line mr-0.5" />配对 {c.pairedNo}
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-500 text-[10px]">未配眼镜</span>
                )}
                <span className="text-xs text-gray-400">{c.online ? "在线" : "离线"} · {c.battery}%</span>
                {incomingSel === c.id && <i className="ri-checkbox-circle-fill text-amber-600" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Seat map */}
      {visibleBoxes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          {activeGroup ? "当前船组未绑定箱子。请在“配置箱子”中绑定后查看设备。" : "尚无已编号设备。点击右上角「初始化编号」按台账顺序分配 A01 起的槽位号。"}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {visibleBoxes.map((box) => {
            // 该箱设备的航线 + 方向分布：用于显示"当前航线·方向"，多组合时显示"混合"
            const installed = box.slots.filter((s) => s.hasHardware);
            const combos = new Set(installed.map((s) => `${s.routeId || ""}|${s.routeDirection || ""}`));
            const onlyCombo = combos.size === 1 ? [...combos][0].split("|") : null;
            const currentRouteId = onlyCombo ? onlyCombo[0] : "";
            const currentDirection = (onlyCombo ? onlyCombo[1] : "") as "" | "up" | "down";
            const currentRoute = routes.find((r) => r.id === currentRouteId);
            const mixed = combos.size > 1;
            const currentDirLabel = currentDirection === "up" ? "上" : currentDirection === "down" ? "下" : "";
            // 测试箱识别：slot 301..312 属于运维用的测试箱，头显示成「测试箱」
            // 而不是普通生产箱，避免误当业务箱使用。
            const isTestBox = box.slots.some((s) => Number(s.slotCode) >= 301);
            const boxLabel = isTestBox ? "测试箱" : rentalBoxLabel(box.box);
            return (
            <div key={box.box} className={`bg-white rounded-xl border p-4 ${isTestBox ? "border-violet-300 bg-violet-50/30" : "border-gray-200"}`}>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={`w-8 h-8 flex items-center justify-center text-white rounded-lg text-sm font-bold ${isTestBox ? "bg-violet-600" : "bg-gray-900"}`}>
                  {isTestBox ? <i className="ri-flask-line" /> : box.box}
                </span>
                <span className="text-sm text-gray-500">
                  {boxLabel} · {box.slots.filter((s) => displayStatus(s) === "available").length}/{box.slots.length} 可用
                </span>
                {/* 当前航线 + 方向徽章 */}
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    mixed
                      ? "bg-amber-50 text-amber-700"
                      : currentRoute
                      ? "bg-teal-50 text-teal-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                  title={
                    mixed
                      ? `该箱内设备绑定了多条航线 / 方向（${combos.size} 组）`
                      : currentRoute
                      ? `当前航线：${currentRoute.name}${currentDirLabel ? " · " + currentDirLabel + "行" : ""}`
                      : "未绑定航线"
                  }
                >
                  <i className="ri-route-line mr-0.5" />
                  {mixed
                    ? `混合 (${combos.size})`
                    : currentRoute
                    ? `${currentRoute.shortName}${currentDirLabel ? " · " + currentDirLabel : ""}`
                    : "未绑定"}
                </span>
                {/* 本箱航线 + 方向选择 */}
                <select
                  disabled={routeBusy || activeRoutes.length === 0 || installed.length === 0}
                  defaultValue=""
                  onChange={(e) => {
                    const v = e.target.value;
                    e.currentTarget.value = "";
                    if (v === "__unbind") { bindRoute("box", box.box, "", "", boxLabel); return; }
                    if (!v) return;
                    const [rid, dir] = v.split("|") as [string, "up" | "down"];
                    bindRoute("box", box.box, rid, dir, boxLabel);
                  }}
                  className="ml-auto px-2 py-1 text-xs border border-gray-200 rounded-md disabled:opacity-50"
                  title={installed.length === 0 ? "本箱无入柜设备" : "切换本箱的航线 + 方向（需二次确认）"}
                >
                  <option value="" disabled>
                    {mixed
                      ? `当前：混合 ${combos.size} 组`
                      : currentRoute
                      ? `当前：${currentRoute.shortName}${currentDirLabel ? " · " + currentDirLabel : ""}`
                      : "未绑定航线"}
                  </option>
                  {activeRoutes.flatMap((r) => [
                    (mixed || currentRoute?.id !== r.id || currentDirection !== "up") ? (
                      <option key={`${r.id}|up`} value={`${r.id}|up`}>切到 {r.shortName} · 上行</option>
                    ) : null,
                    (mixed || currentRoute?.id !== r.id || currentDirection !== "down") ? (
                      <option key={`${r.id}|down`} value={`${r.id}|down`}>切到 {r.shortName} · 下行</option>
                    ) : null,
                  ])}
                  <option value="__unbind">解除本箱绑定</option>
                </select>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-7 md:grid-cols-7 lg:grid-cols-7 gap-1.5">
                {box.slots.map((s) => {
                  const ds = displayStatus(s);
                  const meta = STATUS_META[ds] ?? STATUS_META.disabled;
                  const gs = glassDisplayStatus(s);
                  const gMeta = STATUS_META[gs] ?? STATUS_META.disabled;
                  const slotLabel = fmtSlot(s.slotCode);
                  const handlePhoneClick = async () => {
                    if (incomingSel && ds === "vacant") {
                      try {
                        await apiSend("POST", "/rental/bind", { slotCode: s.slotCode, deviceId: incomingSel });
                        toast(`已装入 ${slotLabel} 号`, "success");
                        setIncomingSel("");
                        await reload();
                      } catch (e) {
                        toast(e instanceof Error ? e.message : "装入失败", "error");
                      }
                      return;
                    }
                    if (opsMode) { openBindDialog(s); return; }
                    if (ds === "vacant") {
                      toast("空置槽位（未放入设备）：先在上方「待入柜设备」选中设备，或开启运维模式绑定", "info");
                      return;
                    }
                    // APP 点“开始旅程”产生的是 APPSTART 使用记录，不会留下
                    // status=borrowed 的传统租借订单。两种在用状态都允许单机归还。
                    if (s.orderId || (s.tripStarted && !s.tripEnded)) {
                      setSelected(s);
                      return;
                    }
	                    toast("该手机当前不在用；如需绑定 / 更换 / 撤出设备，请开启运维模式", "info");
                  };
                  const handleGlassClick = () => {
                    if (opsMode) { void openBindDialog(s); return; }
                    toast("眼镜随手机一并管理；归还请点击上方手机格", "info");
                  };
                  return (
                    <div key={s.slotCode} className={`flex flex-col gap-0.5 ${opsMode ? "ring-1 ring-violet-200 rounded-md" : ""}`}>
                      {/* 手机：上半 */}
                      <button
                        onClick={handlePhoneClick}
                        title={
                          ds === "vacant"
                            ? `${slotLabel}A · 空置（未放入手机）`
	                            : `${slotLabel}A 手机 · ${meta.label} · ${s.deviceNo} · 电量${s.battery}%${s.routeName ? ` · 航线 ${s.routeName}` : ""}`
                        }
                        className={`relative min-h-[34px] sm:h-9 rounded-md text-[11px] font-bold transition-colors ${meta.cls}`}
                      >
                        {slotLabel}A
	                        {ds !== "vacant" && (
                          <span
                            title={`手机电量 ${s.battery}%`}
                            className={`absolute -top-1.5 -right-1 px-1 text-center text-[8px] leading-3.5 rounded-full bg-white/95 border ${
                              s.battery < LOW_BATTERY ? "text-red-500 border-red-200" : s.battery < 60 ? "text-amber-600 border-amber-200" : "text-emerald-600 border-emerald-200"
                            }`}
                          >
                            {s.battery}%
                          </span>
                        )}
                      </button>
                      {/* 眼镜：下半 */}
                      <button
                        onClick={handleGlassClick}
                        title={
                          gs === "vacant"
                            ? `${slotLabel}B · 未配对眼镜`
                            : `${slotLabel}B 眼镜 · ${gMeta.label} · ${s.pairedNo || "-"} · 电量${s.pairedBattery ?? 0}%`
                        }
                        className={`relative min-h-[34px] sm:h-9 rounded-md text-[11px] font-bold transition-colors ${gMeta.cls}`}
                      >
                        {slotLabel}B
                        {/* 在用时同样显示眼镜电量，便于运营在座位图直接判断是否需要换电。 */}
                        {gs !== "vacant" && (
                          <span
                            title={`眼镜电量 ${s.pairedBattery ?? 0}%`}
                            className={`absolute -top-1.5 -right-1 px-1 text-center text-[8px] leading-3.5 rounded-full bg-white/95 border ${
                              (s.pairedBattery ?? 0) < LOW_BATTERY ? "text-red-500 border-red-200" : (s.pairedBattery ?? 0) < 60 ? "text-amber-600 border-amber-200" : "text-emerald-600 border-emerald-200"
                            }`}
                          >
                            {s.pairedBattery ?? 0}%
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Unreturned board */}
      {LEGACY_RENTAL_FLOW_ENABLED && (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-alarm-warning-line text-amber-500" />
          <span className="text-sm font-semibold text-gray-900">未归还 ({unreturned.length})</span>
          <span className="text-xs text-gray-400">到站前广播只报编号，不报身份</span>
        </div>
        {unreturned.length === 0 ? (
          <p className="text-sm text-gray-400">当前没有在借设备。</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {unreturned.map((u) => (
              <span key={u.slotCode} className="px-2.5 py-1.5 bg-amber-50 text-amber-800 rounded-lg text-xs">
                <b>{fmtSlot(u.slotCode)}</b> 号 · {u.borrowedAt} 开始使用 · {feeText(u)}
              </span>
            ))}
          </div>
        )}
      </div>
      )}

      {/* 本组已出借设备 —— 小方块平铺,和 seat board 一样一眼扫完,支持
          多选 / 全选 / 归还所选。5s 轮询同步。 */}
      {LEGACY_RENTAL_FLOW_ENABLED && showGroupLoanList && activeGroup && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center bg-black/40 p-2 sm:p-4"
          onClick={() => setShowGroupLoanList(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full sm:max-w-3xl max-h-[calc(100dvh-env(safe-area-inset-top)-12px)] sm:max-h-[85vh] flex flex-col overscroll-contain"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 gap-2 flex-wrap">
              <h3 className="text-base font-bold text-gray-900">
                <i className="ri-ship-line text-teal-600 mr-1.5" />
                本组已出借 · {activeGroup.name}
                <span className="ml-2 text-xs font-normal text-gray-500">共 {groupUnreturned.length} 台</span>
              </h3>
              <div className="flex items-center gap-2 ml-auto">
                {groupUnreturned.length > 0 && (
                  <>
                    <button
                      onClick={() => {
                        // 全选/全不选 toggle:如果已经全选就取消,否则全选。
                        const allSelected = selectedGroupLoanCount === groupUnreturned.length;
                        if (allSelected) setSelectedInGroupLoan({});
                        else {
                          const all: Record<string, boolean> = {};
                          groupUnreturned.forEach((u) => { all[u.slotCode] = true; });
                          setSelectedInGroupLoan(all);
                        }
                      }}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      {selectedGroupLoanCount === groupUnreturned.length ? "全不选" : "全选"}
                    </button>
                    <button
                      onClick={async () => {
                        const slots = Object.keys(selectedInGroupLoan).filter((k) => selectedInGroupLoan[k]);
                        await returnSelectedSlots(slots);
                      }}
                      disabled={selectedGroupLoanCount === 0 || batchReturnBusy}
                      className="px-3 py-1.5 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-go-back-line mr-1" />
                      归还所选 ({selectedGroupLoanCount})
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowGroupLoanList(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 pb-[max(env(safe-area-inset-bottom),12px)]">
              {groupUnreturned.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-12">
                  <i className="ri-checkbox-circle-line text-2xl block mb-2" />
                  本船组当前没有在借设备
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {groupUnreturned.map((u) => {
                    const on = !!selectedInGroupLoan[u.slotCode];
                    const isReception = u.orderNo.startsWith("RCP-") || u.depositCents === 0;
                    return (
                      <button
                        key={u.orderNo}
                        onClick={() => setSelectedInGroupLoan((prev) => ({ ...prev, [u.slotCode]: !prev[u.slotCode] }))}
                        title={`${u.deviceNo || ""}\n开始使用于 ${u.borrowedAtFull || u.borrowedAt}${isReception ? "\n接待登记" : `\n凭证 ${u.orderNo}`}`}
                        className={`relative aspect-square rounded-lg border-2 flex flex-col items-center justify-center font-mono transition ${
                          on
                            ? "bg-red-500 text-white border-red-600 shadow-md scale-[1.03]"
                            : "bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"
                        }`}
                      >
                        {on && (
                          <i className="ri-check-line absolute top-1 right-1 text-white text-sm" />
                        )}
                        {isReception && (
                          <i className="ri-user-star-line absolute top-1 left-1 text-teal-600 text-xs" />
                        )}
                        <span className="text-base font-bold leading-none">{fmtSlot(u.slotCode)}</span>
                        <span className="text-[10px] opacity-80 mt-0.5">
                          {(u.borrowedAtFull || u.borrowedAt || "").slice(-5) /* HH:MM */}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 已出借设备列表抽屉 — 手机底部抽屉/桌面右侧抽屉。
          通过 reload 5s 轮询保持同步，4 名 ops 同时操作时见的都是最新数据。 */}
      {LEGACY_RENTAL_FLOW_ENABLED && showLoanList && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end bg-black/30"
          onClick={() => setShowLoanList(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-none shadow-xl w-full sm:w-[28rem] sm:h-full max-h-[calc(100dvh-env(safe-area-inset-top)-12px)] sm:max-h-none flex flex-col overscroll-contain"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">
                <i className="ri-list-check-2 text-teal-600 mr-1.5" />
                已出借设备
                <span className="ml-2 text-xs font-normal text-gray-500">共 {unreturned.length} 台</span>
              </h3>
              <button
                onClick={() => setShowLoanList(false)}
                className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2 pb-[max(env(safe-area-inset-bottom),12px)]">
              {unreturned.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-12">
                  <i className="ri-checkbox-circle-line text-2xl block mb-2" />
                  当前没有在借设备
                </div>
              ) : (
                unreturned.map((u) => (
                  <div key={u.orderNo} className="border border-gray-200 rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-base font-bold text-gray-900">
                        {fmtSlot(u.slotCode) || u.deviceNo} 号
                      </span>
                      <span className="text-xs text-gray-400">
                        {u.deviceNo ? `· ${u.deviceNo}` : ""}
                      </span>
                      <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        使用中
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      <i className="ri-time-line mr-1" />
                      {u.borrowedAtFull || u.borrowedAt}
                      <span className="mx-2 text-gray-300">|</span>
                      {u.orderNo.startsWith("RCP-") || u.depositCents === 0
                        ? <span className="text-teal-700">接待登记</span>
                        : <>{feeText(u)}</>}
                    </div>
                    {!(u.orderNo.startsWith("RCP-") || u.depositCents === 0) && (
                      <div className="text-xs text-gray-500 break-all">
                        <i className="ri-qr-code-line mr-1" />
                        支付凭证：<span className="font-mono text-gray-700">{u.orderNo}</span>
                      </div>
                    )}
                    <button
                      onClick={async () => {
                        await onScanReturn(u);
                      }}
                      disabled={scanBusy}
                      className="w-full mt-2 px-3 py-2 min-h-[44px] bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 disabled:opacity-50 active:scale-95 transition-transform"
                    >
                      <i className="ri-arrow-go-back-line mr-1" />
                      {u.orderNo.startsWith("RCP-") || u.depositCents === 0 ? "归还" : "归还 · 退押金"}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ops-mode bind dialog — 手机底部抽屉，桌面居中弹窗 */}
      {bindSlot && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30" onClick={() => setBindSlot(null)}>
          <div
            className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:w-[26rem] max-h-[calc(100dvh-env(safe-area-inset-top)-12px)] sm:max-h-[90vh] overflow-y-auto overscroll-contain p-5 space-y-4 pb-[max(env(safe-area-inset-bottom),20px)] sm:pb-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">
                <i className="ri-tools-line text-violet-600 mr-1.5" />
                {fmtSlot(bindSlot.slotCode)} 号 · 设备绑定
              </h3>
              <button onClick={() => setBindSlot(null)} className="text-gray-300 hover:text-gray-500">
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="px-3 py-2 rounded-lg bg-gray-50 text-sm text-gray-600 space-y-1">
              <div>
                <span className="text-xs text-gray-400 mr-1">A 手机：</span>
                {bindSlot.hasHardware ? (
                  <>
                    <b className="text-gray-800 font-mono">{bindSlot.deviceNo}</b>
                    <span className={`ml-2 text-xs ${bindSlot.online ? "text-emerald-600" : "text-gray-400"}`}>
                      {bindSlot.online ? "在线" : "离线"} · 电量 {bindSlot.battery}%
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400">空置（未放入手机）</span>
                )}
              </div>
              <div>
                <span className="text-xs text-gray-400 mr-1">B 眼镜：</span>
                {bindSlot.pairedHasHardware && bindSlot.pairedNo ? (
                  <>
                    <b className="text-gray-800 font-mono">{bindSlot.pairedNo}</b>
                    <span className={`ml-2 text-xs ${bindSlot.pairedOnline ? "text-emerald-600" : "text-gray-400"}`}>
                      {bindSlot.pairedOnline ? "在线" : "离线"} · 电量 {bindSlot.pairedBattery ?? 0}%
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400">未配对眼镜</span>
                )}
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="text-sm font-semibold text-gray-700 mb-2">
                <i className="ri-smartphone-line text-violet-600 mr-1" />
                手机绑定 / 替换（A 位）
              </div>
              <div className="text-xs text-gray-400 mb-2">
                眼镜（B 位）与手机出厂时已 BLE 一对一配对，由手机端自动上报，无需在此手动选择。
              </div>
              <input
                value={candQuery}
                onChange={(e) => setCandQuery(e.target.value)}
                placeholder="搜索台账编号 / 型号…"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2"
              />
              <div className="max-h-64 overflow-y-auto space-y-1">
                {candidates
                  .filter((c) => !candQuery || c.deviceNo.includes(candQuery) || c.model.includes(candQuery))
                  .map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setCandSel(c.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-sm ${
                        candSel === c.id ? "border-violet-400 bg-violet-50" : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full shrink-0 ${c.online ? "bg-emerald-500" : "bg-gray-300"}`} />
                      <span className="font-mono font-medium text-gray-800">{c.deviceNo}</span>
                      {c.pairedNo ? (
                        <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-600 text-[10px] shrink-0">
                          <i className="ri-glasses-line mr-0.5" />{c.pairedNo}
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-500 text-[10px] shrink-0">未配眼镜</span>
                      )}
                      <span className="text-xs text-gray-400 truncate">{c.model}</span>
                      <span className="ml-auto text-xs text-gray-500 shrink-0">{c.battery}%</span>
                    </button>
                  ))}
                {candidates.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">
                    暂无可绑定设备（需设备已开机上报且未绑定其他槽位）
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              {bindSlot.hasHardware && (
                <button onClick={onUnbind} className="px-3 py-2 min-h-[48px] sm:min-h-0 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200 active:scale-95 transition-transform">
                  撤出设备
                </button>
              )}
              <button
                onClick={onBind}
                disabled={!candSel}
                className="flex-1 px-4 py-2 min-h-[48px] sm:min-h-0 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50 active:scale-95 transition-transform"
              >
                绑定所选设备
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 单机归还 — 点击箱内存在未归还订单的手机后打开。 */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30" onClick={() => setSelected(null)}>
          <div
            className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:w-96 max-h-[calc(100dvh-env(safe-area-inset-top)-12px)] sm:max-h-[90vh] overflow-y-auto overscroll-contain p-5 space-y-4 pb-[max(env(safe-area-inset-bottom),20px)] sm:pb-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">
                单机归还
              </h3>
              <button type="button" onClick={() => setSelected(null)} aria-label="关闭单机归还" className="w-10 h-10 -mr-2 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 text-sm text-gray-700 space-y-1">
              <div><span className="text-gray-500">手机：</span><b className="font-mono">{selected.deviceNo || "—"}</b></div>
              <div><span className="text-gray-500">槽位：</span>{fmtSlot(selected.slotCode)} 号</div>
              <div><span className="text-gray-500">状态：</span><b>{selected.tripStarted && !selected.tripEnded ? "旅程中" : "待归还"}</b></div>
              {selected.orderNo && <div><span className="text-gray-500">未归还订单：</span><b className="font-mono break-all">{selected.orderNo}</b></div>}
              <div><span className="text-gray-500">开始时间：</span>{selected.borrowedAt || "—"}</div>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">归还后会结束这台手机的当前旅程，并下发清除游客数据指令；不会影响同箱其他手机。</p>
            <button onClick={() => onReturn(selected)} className="w-full px-4 py-3 min-h-[48px] bg-red-500 text-white rounded-lg text-base font-medium hover:bg-red-600 active:scale-[0.98] transition-transform">
              <i className="ri-arrow-go-back-line mr-1" />归还此手机并清除数据
            </button>
          </div>
        </div>
      )}

      {/* 摄像头扫码（lazy-loaded，仅按下 📷 后才下载 html5-qrcode） */}
      {LEGACY_RENTAL_FLOW_ENABLED && cameraOpen && (
        <Suspense fallback={<div className="fixed inset-0 z-[60] bg-black/80 text-white flex items-center justify-center">加载摄像头…</div>}>
          <ScanCamera
            onClose={() => setCameraOpen(false)}
            onScan={(text) => {
              const v = text.trim();
              setScanVoucher(v);
              setCameraOpen(false);
              // 识别后：若 voucher / 设备号已在借中，直接进入归还流程；否则把焦点移到设备号
              setTimeout(() => {
                const slot3 = /^\d{1,3}$/.test(v) ? v.padStart(3, "0") : "";
                const hit = unreturned.find(
                  (u) => u.orderNo === v || u.deviceNo === v || (slot3 && u.slotCode === slot3),
                );
                if (hit) {
                  onScanReturn(hit);
                } else {
                  const el = document.getElementById("scan-device-no") as HTMLInputElement | null;
                  el?.focus();
                }
              }, 50);
            }}
          />
        </Suspense>
      )}
      <CreateShipGroupModal open={showCreateGroup} onClose={() => setShowCreateGroup(false)} onCreate={onCreateShipGroup} routes={activeRoutes} />
      <EditShipGroupBoxesModal
        open={showEditGroupBoxes}
        group={activeGroup}
        busy={scopeBusy}
        onClose={() => setShowEditGroupBoxes(false)}
        onSave={onSaveGroupBoxes}
      />
      <ManageTriggersModal open={showManageTriggers} onClose={() => setShowManageTriggers(false)} triggers={triggers} onReload={reloadTriggers} activeGroup={activeGroup} />
      <ManageTemplatesModal
        open={showTemplates}
        onClose={() => setShowTemplates(false)}
        activeGroupsCount={shipGroups.length}
        onAfterChange={async () => { await reloadTriggers(); }}
      />
      {LEGACY_RENTAL_FLOW_ENABLED && (
        <ReceptionBorrowModal
          open={showReception}
          onClose={() => setShowReception(false)}
          activeGroup={activeGroup}
          onDone={async () => { await reload(); }}
        />
      )}
      {/* 批量归还 —— 选箱确认弹窗,和批量发放同构。默认勾选所有 borrowed > 0 的箱。 */}
      {LEGACY_RENTAL_FLOW_ENABLED && showBatchReturnModal && activeGroup && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => !batchReturnBusy && setShowBatchReturnModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <i className="ri-restart-line text-2xl text-red-500" />
              <div className="text-lg font-semibold text-gray-900">批量归还 · {activeGroup.name}</div>
              <button className="ml-auto text-gray-400 hover:text-gray-700 p-1" onClick={() => setShowBatchReturnModal(false)}>
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="text-xs text-gray-500 leading-relaxed">
              勾选要归还的箱组,确认后箱内所有 <b className="text-amber-600">在借</b> 设备会一次性归还,同时向每台设备下发清除数据指令。
            </div>
            {(() => {
              const returnable = batchPreview.filter((b) => b.borrowed > 0);
              if (returnable.length === 0) {
                return <div className="text-sm text-gray-400 text-center py-6">该船组当前无在借设备</div>;
              }
              return (
                <>
                  <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                    {returnable.map((b) => (
                      <label key={b.box} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={!!batchSelectedBoxes[b.box]}
                          onChange={(e) => setBatchSelectedBoxes((prev) => ({ ...prev, [b.box]: e.target.checked }))}
                          className="w-4 h-4 accent-red-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900">{rentalBoxLabel(b.box)}</div>
                          <div className="text-xs text-gray-500">在借 <b className="text-amber-600">{b.borrowed}</b> 台</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <button
                      onClick={() => {
                        const all: Record<number, boolean> = {};
                        returnable.forEach((b) => { all[b.box] = true; });
                        setBatchSelectedBoxes(all);
                      }}
                      className="text-red-600 hover:underline"
                    >全选</button>
                    <span className="text-gray-300">·</span>
                    <button
                      onClick={() => setBatchSelectedBoxes({})}
                      className="text-gray-500 hover:underline"
                    >全不选</button>
                    <span className="ml-auto text-gray-600">
                      本次将归还 <b className="text-red-600 text-base">
                        {returnable.reduce((s, b) => s + (batchSelectedBoxes[b.box] ? b.borrowed : 0), 0)}
                      </b> 台
                    </span>
                  </div>
                </>
              );
            })()}
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setShowBatchReturnModal(false)}
                disabled={batchReturnBusy}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              >取消</button>
              <button
                onClick={onConfirmBatchReturn}
                disabled={batchReturnBusy || Object.values(batchSelectedBoxes).every((v) => !v)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 disabled:opacity-50"
              >{batchReturnBusy ? "归还中…" : "确认归还并清除数据"}</button>
            </div>
          </div>
        </div>
      )}

      {/* 批量发放 —— 选箱确认弹窗。默认全选,取消勾就是要少发某箱。 */}
      {LEGACY_RENTAL_FLOW_ENABLED && showBatchIssueModal && activeGroup && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => !batchIssueBusy && setShowBatchIssueModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <i className="ri-truck-line text-2xl text-teal-600" />
              <div className="text-lg font-semibold text-gray-900">批量发放 · {activeGroup.name}</div>
              <button className="ml-auto text-gray-400 hover:text-gray-700 p-1" onClick={() => setShowBatchIssueModal(false)}>
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="text-xs text-gray-500 leading-relaxed">
              勾选要发出去的箱组,确认后箱内所有 <b className="text-emerald-600">可发</b> 设备会一次性借出并绑定到本船组。
              航线不匹配 / 已借出 / 空位的设备不受影响。
            </div>
            {batchPreview.length === 0 && (
              <div className="text-sm text-gray-400 text-center py-6">该船组的航线下暂无匹配箱组</div>
            )}
            {batchPreview.length > 0 && (
              <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                {batchPreview.map((b) => {
                  const totalIssuable = b.available;
                  return (
                    <label key={b.box} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${totalIssuable === 0 ? "opacity-50" : "hover:bg-gray-50"}`}>
                      <input
                        type="checkbox"
                        disabled={totalIssuable === 0}
                        checked={!!batchSelectedBoxes[b.box]}
                        onChange={(e) => setBatchSelectedBoxes((prev) => ({ ...prev, [b.box]: e.target.checked }))}
                        className="w-4 h-4 accent-teal-600"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">{rentalBoxLabel(b.box)}</div>
                        <div className="text-xs text-gray-500 flex gap-3">
                          <span>可发 <b className="text-emerald-600">{b.available}</b></span>
                          <span>在借 <b className="text-amber-600">{b.borrowed}</b></span>
                          {b.vacant > 0 && <span>空位 <b className="text-gray-400">{b.vacant}</b></span>}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {batchPreview.length > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <button
                  onClick={() => {
                    const all: Record<number, boolean> = {};
                    batchPreview.forEach((b) => { if (b.available > 0) all[b.box] = true; });
                    setBatchSelectedBoxes(all);
                  }}
                  className="text-teal-700 hover:underline"
                >全选</button>
                <span className="text-gray-300">·</span>
                <button
                  onClick={() => setBatchSelectedBoxes({})}
                  className="text-gray-500 hover:underline"
                >全不选</button>
                <span className="ml-auto text-gray-600">
                  本次将发放 <b className="text-teal-700 text-base">
                    {batchPreview.reduce((s, b) => s + (batchSelectedBoxes[b.box] ? b.available : 0), 0)}
                  </b> 台
                </span>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setShowBatchIssueModal(false)}
                disabled={batchIssueBusy}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              >取消</button>
              <button
                onClick={onConfirmBatchIssue}
                disabled={batchIssueBusy || Object.values(batchSelectedBoxes).every((v) => !v)}
                className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 disabled:opacity-50"
              >{batchIssueBusy ? "发放中…" : "确认发放"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ManageTriggersModal({ open, onClose, triggers, onReload, activeGroup }: {
  open: boolean;
  onClose: () => void;
  triggers: NarrationTrigger[];
  onReload: () => Promise<void>;
  activeGroup?: ShipGroup;
}) {
  const [name, setName] = useState("");
  const [sceneName, setSceneName] = useState("");
  const [busy, setBusy] = useState(false);
  const [sceneOptions, setSceneOptions] = useState<{ baseName: string; seq: number }[]>([]);
  const [optsBusy, setOptsBusy] = useState(false);
  useEffect(() => { if (open) { setName(""); setSceneName(""); setBusy(false); } }, [open]);
  // 拉当前 active 船组的航线 + 方向下点位，下拉 options。无 group 时清空。
  useEffect(() => {
    if (!open || !activeGroup?.routeId) { setSceneOptions([]); return; }
    let cancelled = false;
    setOptsBusy(true);
    apiGet<{ baseName: string; seq: number }[]>(
      `/routes/${activeGroup.routeId}/trigger-scene-options${
        activeGroup.routeDirection ? `?direction=${activeGroup.routeDirection}` : ""
      }`,
    )
      .then((d) => { if (!cancelled) setSceneOptions(d || []); })
      .catch(() => { if (!cancelled) setSceneOptions([]); })
      .finally(() => { if (!cancelled) setOptsBusy(false); });
    return () => { cancelled = true; };
  }, [open, activeGroup?.routeId, activeGroup?.routeDirection]);
  if (!open) return null;
  const noGroup = !activeGroup;
  const noRoute = !activeGroup?.routeId;
  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !sceneName.trim()) { toast("名称和场景名都必填", "info"); return; }
    setBusy(true);
    try {
      await apiSend("POST", "/narration-triggers", { name: name.trim(), sceneName: sceneName.trim(), shipGroupId: activeGroup?.id });
      setName(""); setSceneName("");
      await onReload();
    } catch (err) { toast(err instanceof Error ? err.message : "创建失败", "error"); }
    finally { setBusy(false); }
  };
  const toggleEnabled = async (t: NarrationTrigger) => {
    try {
      await apiSend("PUT", `/narration-triggers/${t.id}`, { enabled: !t.enabled });
      await onReload();
    } catch (e) { toast(e instanceof Error ? e.message : "更新失败", "error"); }
  };
  const del = async (t: NarrationTrigger) => {
    if (t.templateId) {
      toast("默认分发的触发器不能在船组里删除,请到「触发器模板」里删除", "info");
      return;
    }
    if (!(await confirmDialog({
      title: "删除本船组触发器",
      message: `确认删除触发器「${t.name}」→ ${t.sceneName}？\n这是本船组私建的触发器,不影响模板池和其它船组。`,
      confirmText: "删除",
      danger: true,
    }))) return;
    try {
      await apiSend("DELETE", `/narration-triggers/${t.id}`);
      toast("已删除", "success");
      await onReload();
    } catch (e) { toast(e instanceof Error ? e.message : "删除失败", "error"); }
  };
  const dirLabel = activeGroup?.routeDirection === "up" ? "上行" : activeGroup?.routeDirection === "down" ? "下行" : "";
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 p-3 sm:p-4 pt-[max(env(safe-area-inset-top),12px)] pb-[max(env(safe-area-inset-bottom),12px)]" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full max-w-xl max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-24px)] overflow-y-auto overscroll-contain p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">本船组触发器</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><i className="ri-close-line" /></button>
        </div>
        <p className="text-xs text-gray-500">
          每个触发器是「按钮名 → 场景名」的映射。下发时手机按本地方向选 <code>场景名·上</code> 或 <code>场景名·下</code>，没有后缀时回退到原名。
        </p>
        {noGroup ? (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-3 py-2 text-xs">
            <i className="ri-error-warning-line mr-1" />请先在上方选择 / 新建船组（船组绑定航线后，场景名下拉才能列出该航线的点位）。
          </div>
        ) : noRoute ? (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-3 py-2 text-xs">
            <i className="ri-error-warning-line mr-1" />当前船组「{activeGroup.name}」未绑定航线（旧数据）。请新建带航线的船组再添加触发器。
          </div>
        ) : (
          <div className="bg-teal-50 border border-teal-200 text-teal-700 rounded-lg px-3 py-2 text-xs">
            <i className="ri-route-line mr-1" />当前船组航线：<strong>{activeGroup.routeName}</strong>
            {dirLabel ? <> · <strong>{dirLabel}</strong></> : null}
            。场景名从该航线点位中下拉选择，避免手输错字。
          </div>
        )}

        <form onSubmit={add} className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">按钮名 *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：大坝解说" className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">场景名 *</label>
              <select
                value={sceneName}
                onChange={(e) => setSceneName(e.target.value)}
                disabled={noRoute || optsBusy || sceneOptions.length === 0}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm bg-white disabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                <option value="" disabled>
                  {noRoute
                    ? "请先绑定航线"
                    : optsBusy
                    ? "加载中…"
                    : sceneOptions.length === 0
                    ? "该航线 / 方向下没有点位"
                    : "请选择场景"}
                </option>
                {sceneOptions.map((o) => (
                  <option key={o.baseName} value={o.baseName}>{o.baseName}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" disabled={busy || noRoute || !name.trim() || !sceneName.trim()} className="w-full px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60">
            <i className="ri-add-line mr-1" />{busy ? "添加中…" : "添加触发器"}
          </button>
        </form>

        <div className="max-h-80 overflow-y-auto space-y-2">
          {triggers.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-6">还没有触发器</div>
          ) : triggers.map((t) => (
            <div key={t.id} className={`border rounded-lg p-3 ${t.enabled ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-900">{t.name}</span>
                <span className="text-xs text-gray-500">→ {t.sceneName}</span>
                {t.templateId ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">默认分发</span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">本船组私建</span>
                )}
                <div className="ml-auto flex items-center gap-1.5 text-xs">
                  <button
                    onClick={() => toggleEnabled(t)}
                    className="px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50"
                  >
                    {t.enabled ? "停用" : "启用"}
                  </button>
                  <button
                    onClick={() => del(t)}
                    disabled={!!t.templateId}
                    title={t.templateId ? "默认分发的触发器请去「触发器模板」里删除" : "删除触发器"}
                    className="px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    <i className="ri-delete-bin-line mr-0.5" />删除
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// 触发器模板池: 在这里增/改/删都"必定"同步到所有 active 船组(含新建未来)
// —— 模板池的语义就是"对所有船组生效",所以不再让用户选 propagate,只在动作
// 前弹确认告知会影响 N 个 active 船组。新建船组时后端会自动复制全套模板。
// 触发器模板的场景下拉用的最小 Scene shape。Scene 类型从 mocks 那边搬过来字段
// 太多,这里只需 name 用于显示/匹配,id 用于稳定 key。
type SceneOption = { id: string; name: string; status: string };

function ManageTemplatesModal({ open, onClose, activeGroupsCount, onAfterChange }: {
  open: boolean;
  onClose: () => void;
  activeGroupsCount: number;
  onAfterChange: () => Promise<void>;
}) {
  const [list, setList] = useState<NarrationTrigger[]>([]);
  const [scenes, setScenes] = useState<SceneOption[]>([]);
  const [name, setName] = useState("");
  const [sceneName, setSceneName] = useState("");
  const [busy, setBusy] = useState(false);
  // 行内编辑:editingId 命中的行展开为 name input + scene select,其它行只读。
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingScene, setEditingScene] = useState("");
  const reload = async () => {
    try { setList(await apiGet<NarrationTrigger[]>("/narration-triggers")); } catch { /* keep */ }
  };
  const reloadScenes = async () => {
    try {
      const all = await apiGet<SceneOption[]>("/scenes");
      // active 在前,其余隐藏(避免运营误选已下线点位)。按名字排序便于查找。
      setScenes(all
        .filter((s) => s.status === "active")
        .sort((a, b) => a.name.localeCompare(b.name, "zh")));
    } catch { /* keep stale list — 仍可手输回退 */ }
  };
  useEffect(() => {
    if (open) {
      setName(""); setSceneName(""); setBusy(false);
      setEditingId(null); setEditingName(""); setEditingScene("");
      void reload(); void reloadScenes();
    }
  }, [open]);
  if (!open) return null;
  const groupSuffix = activeGroupsCount > 0 ? `，将同步到 ${activeGroupsCount} 个 active 船组` : "";
  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !sceneName.trim()) { toast("名称和场景名都必填", "info"); return; }
    if (!(await confirmDialog({
      title: "新增模板触发器",
      message: `新增「${name.trim()}」→「${sceneName.trim()}」${groupSuffix}。是否继续？`,
      confirmText: "新增并同步",
    }))) return;
    setBusy(true);
    try {
      await apiSend("POST", "/narration-triggers?propagate=true", { name: name.trim(), sceneName: sceneName.trim() });
      setName(""); setSceneName("");
      toast("已新增并同步", "success");
      await reload(); await onAfterChange();
    } catch (err) { toast(err instanceof Error ? err.message : "新增失败", "error"); }
    finally { setBusy(false); }
  };
  const toggleEnabled = async (t: NarrationTrigger) => {
    try {
      await apiSend("PUT", `/narration-triggers/${t.id}`, { enabled: !t.enabled });
      await reload();
    } catch (e) { toast(e instanceof Error ? e.message : "更新失败", "error"); }
  };
  const startEdit = (t: NarrationTrigger) => {
    setEditingId(t.id);
    setEditingName(t.name);
    setEditingScene(t.sceneName);
  };
  const cancelEdit = () => { setEditingId(null); setEditingName(""); setEditingScene(""); };
  const saveEdit = async (t: NarrationTrigger) => {
    const newName = editingName.trim();
    const newScene = editingScene.trim();
    if (!newName || !newScene) { toast("名称和场景名都必填", "info"); return; }
    if (newName === t.name && newScene === t.sceneName) { cancelEdit(); return; }
    if (!(await confirmDialog({
      title: "保存模板修改",
      message: `保存「${newName}」→「${newScene}」${groupSuffix}。是否继续？`,
      confirmText: "保存并同步",
    }))) return;
    try {
      await apiSend("PUT", `/narration-triggers/${t.id}?propagate=true`, { name: newName, sceneName: newScene });
      toast("已保存并同步", "success");
      cancelEdit();
      await reload(); await onAfterChange();
    } catch (e) { toast(e instanceof Error ? e.message : "保存失败", "error"); }
  };
  const del = async (t: NarrationTrigger) => {
    if (!(await confirmDialog({
      title: `删除模板「${t.name}」？`,
      message: `将删除模板,并同步删除所有 active 船组(${activeGroupsCount} 个)下指向此模板的触发器实例。dismissed 船组的历史触发器保留。`,
      confirmText: "删除并同步",
      danger: true,
    }))) return;
    try {
      await apiSend("DELETE", `/narration-triggers/${t.id}?propagate=true`);
      toast("已删除并同步", "success");
      await reload(); await onAfterChange();
    } catch (e) { toast(e instanceof Error ? e.message : "删除失败", "error"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 p-3 sm:p-4 pt-[max(env(safe-area-inset-top),12px)] pb-[max(env(safe-area-inset-bottom),12px)]" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full max-w-xl max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-24px)] overflow-y-auto overscroll-contain p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">触发器模板</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><i className="ri-close-line" /></button>
        </div>
        <p className="text-xs text-gray-500">
          这里维护的是<strong>所有船组通用</strong>的触发器。新增 / 修改 / 删除都会同步到当前 <strong>{activeGroupsCount}</strong> 个 active 船组,新建船组时也会整套自动复制。
        </p>

        <form onSubmit={add} className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">按钮名 *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：大坝解说" className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">场景名 *</label>
              {/* 下拉源自后端 active scenes,避免手输字符串错字导致 mobile 端
                  manualTriggerSceneNames 匹配不上、geofence 还是自动播报。 */}
              <select value={sceneName} onChange={(e) => setSceneName(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                <option value="">— 请选择点位 —</option>
                {scenes.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" disabled={busy || !name.trim() || !sceneName.trim()} className="w-full px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60">
            <i className="ri-add-line mr-1" />{busy ? "新增中…" : "新增并同步到所有船组"}
          </button>
        </form>

        <div className="max-h-80 overflow-y-auto space-y-2">
          {list.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-6">还没有模板,新增一个开始</div>
          ) : list.map((t) => {
            const isEditing = editingId === t.id;
            // 行内编辑:scene 下拉里若包含历史"已被改名/已下线"的旧名,补一个
            // 选项保留原值,运营改保存时再换成真实点位。
            const editSceneInList = scenes.some((s) => s.name === editingScene);
            return (
              <div key={t.id} className={`border rounded-lg p-3 ${t.enabled ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
                {isEditing ? (
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,auto] gap-2 items-end">
                    <div>
                      <label className="block text-[11px] text-gray-500 mb-1">按钮名 *</label>
                      <input value={editingName} onChange={(e) => setEditingName(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500 mb-1">场景名 *</label>
                      <select value={editingScene} onChange={(e) => setEditingScene(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                        {!editSceneInList && editingScene !== "" && (
                          <option value={editingScene}>{editingScene}（已下线/不在 active 点位）</option>
                        )}
                        <option value="">— 请选择点位 —</option>
                        {scenes.map((s) => (
                          <option key={s.id} value={s.name}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-1.5 text-xs">
                      <button onClick={() => saveEdit(t)} className="px-2 py-1 bg-teal-600 text-white rounded hover:bg-teal-700"><i className="ri-check-line mr-0.5" />保存</button>
                      <button onClick={cancelEdit} className="px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50">取消</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900">{t.name}</span>
                    <span className="text-xs text-gray-500">→ {t.sceneName}</span>
                    <div className="ml-auto flex items-center gap-1.5 text-xs">
                      <button onClick={() => toggleEnabled(t)} className="px-2 py-1 border border-gray-200 text-gray-600 rounded hover:bg-gray-50">{t.enabled ? "停用" : "启用"}</button>
                      <button onClick={() => startEdit(t)} className="px-2 py-1 border border-teal-200 text-teal-700 rounded hover:bg-teal-50"><i className="ri-pencil-line mr-0.5" />改</button>
                      <button onClick={() => del(t)} className="px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50"><i className="ri-delete-bin-line mr-0.5" />删除</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-gray-400 leading-relaxed">
          启用/停用只影响模板自身(影响新建船组时是否复制)。各船组里这条触发器是否启用,在「本船组触发器」里单独控制。
        </p>
      </div>
    </div>
  );
}

function CreateShipGroupModal({ open, onClose, onCreate, routes }: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string, routeId: string, routeDirection: "up" | "down", boxes: number[]) => Promise<void>;
  routes: RouteLite[];
}) {
  const [name, setName] = useState("");
  const [routeId, setRouteId] = useState("");
  const [direction, setDirection] = useState<"" | "up" | "down">("");
  const [boxes, setBoxes] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setName(""); setRouteId(""); setDirection(""); setBoxes([]); setBusy(false); }
  }, [open]);
  if (!open) return null;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (!routeId) { toast("请选择航线", "info"); return; }
    if (direction !== "up" && direction !== "down") { toast("请选择上行或下行", "info"); return; }
    if (boxes.length === 0) { toast("请至少绑定一个箱子", "info"); return; }
    setBusy(true);
    try { await onCreate(name.trim(), routeId, direction, boxes); onClose(); }
    catch (err) { toast(err instanceof Error ? err.message : "创建失败", "error"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 p-3 sm:p-4 pt-[max(env(safe-area-inset-top),12px)] pb-[max(env(safe-area-inset-bottom),12px)]" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-24px)] overflow-y-auto overscroll-contain p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-gray-900">新建船组</h3>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">船组名 *</label>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：渝胜 2 号 0623 班次" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
            <p className="text-xs text-gray-400 mt-1">建议带船号 + 班次，方便区分。本船组的所有借出/归还都会挂到这里。</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">绑定箱子 *</label>
            <BoxPicker value={boxes} onChange={setBoxes} disabled={busy} />
            <p className="text-xs text-gray-400 mt-1">同一箱子只能绑定一个在用船组；同船播放、后台触发和批量操作都只作用于这些箱子。</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">航线 *</label>
            <select value={routeId} onChange={(e) => setRouteId(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
              <option value="" disabled>{routes.length === 0 ? "暂无可用航线" : "请选择航线"}</option>
              {routes.map((r) => (
                <option key={r.id} value={r.id}>{r.name}（{r.shortName}）</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">方向 *</label>
            <div className="flex gap-2">
              {(["up", "down"] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDirection(d)}
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                    direction === d
                      ? "border-teal-500 bg-teal-50 text-teal-700"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {d === "up" ? "上行" : "下行"}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">扫码借出时会校验设备所在分箱与本航线 / 方向一致，不一致拒绝并提示「航线有误」。</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">取消</button>
            <button type="submit" disabled={busy || !name.trim() || !routeId || boxes.length === 0 || (direction !== "up" && direction !== "down")} className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60">{busy ? "创建中…" : "创建并激活"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditShipGroupBoxesModal({ open, group, busy, onClose, onSave }: {
  open: boolean;
  group?: ShipGroup;
  busy: boolean;
  onClose: () => void;
  onSave: (boxes: number[]) => Promise<void>;
}) {
  const [boxes, setBoxes] = useState<number[]>([]);
  useEffect(() => {
    if (open) setBoxes(group?.boxes ?? []);
    // 船组列表会每 5 秒轮询一次，轮询返回的是新的 boxes 数组引用。
    // 只在打开弹窗或切换船组时初始化，不能让轮询覆盖用户尚未保存的选择。
  }, [open, group?.id]);
  if (!open || !group) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 p-3 sm:p-4 pt-[max(env(safe-area-inset-top),12px)] pb-[max(env(safe-area-inset-bottom),12px)]" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-24px)] overflow-y-auto overscroll-contain p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <div>
            <h3 className="text-base font-bold text-gray-900">配置绑定箱子</h3>
            <p className="text-xs text-gray-500 mt-0.5">船组：{group.name}</p>
          </div>
          <button type="button" aria-label="关闭" onClick={onClose} disabled={busy} className="ml-auto p-2 -mr-2 text-gray-400 hover:text-gray-700 disabled:opacity-50">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <BoxPicker value={boxes} onChange={setBoxes} disabled={busy} />
        <p className="text-xs text-gray-400 leading-relaxed">未选择任何箱子即解除本船组全部箱子绑定。保存后，同船播放、后台触发和批量操作将只作用于已选箱子。</p>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-60">取消</button>
          <button type="button" onClick={() => onSave(boxes)} disabled={busy} className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60">{busy ? "保存中…" : "保存"}</button>
        </div>
      </div>
    </div>
  );
}

function BoxPicker({ value, onChange, disabled = false }: {
  value: number[];
  onChange: (boxes: number[]) => void;
  disabled?: boolean;
}) {
  const allBoxes = useMemo(() => Array.from({ length: TEST_BOX_NUMBER }, (_, index) => index + 1), []);
  const selected = useMemo(() => new Set(value), [value]);
  const toggle = (box: number) => {
    if (disabled) return;
    onChange(selected.has(box) ? value.filter((item) => item !== box) : [...value, box].sort((a, b) => a - b));
  };
  const selectAll = () => onChange(allBoxes);
  const clear = () => onChange([]);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-800">已选 {value.length} 个箱子</span>
        <div className="ml-auto flex items-center gap-1.5">
          <button type="button" onClick={selectAll} disabled={disabled || value.length === allBoxes.length} className="px-2.5 py-1.5 min-h-[36px] rounded-md border border-teal-200 bg-white text-xs font-medium text-teal-700 hover:bg-teal-50 disabled:opacity-50">全选</button>
          <button type="button" onClick={clear} disabled={disabled || value.length === 0} className="px-2.5 py-1.5 min-h-[36px] rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50">清空</button>
        </div>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2" role="group" aria-label="选择箱子">
        {allBoxes.map((box) => {
          const isSelected = selected.has(box);
          return (
            <button
              key={box}
              type="button"
              onClick={() => toggle(box)}
              disabled={disabled}
              aria-pressed={isSelected}
              className={`min-h-[44px] rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 ${
                isSelected
                  ? "border-teal-600 bg-teal-600 text-white shadow-sm"
                  : "border-gray-200 bg-white text-gray-700 hover:border-teal-300 hover:bg-teal-50"
              }`}
            >
              {box === TEST_BOX_NUMBER ? "测试箱" : `箱 ${box}`}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// 接待登记弹窗：接待人员手机号 + 一串设备号（空格 / 逗号 / 换行分隔）批量借出。
// 用于无微信支付凭证的接待场景（VIP / 内部 / 试用）。手机号是接待人员的、不是
// 客户的——客户身份不在前台录入。后端为每台设备合成
// order_no="RCP-{phone}-{ts}-{idx}"、deposit=0、payment_type='reception'。
function ReceptionBorrowModal({ open, onClose, activeGroup, onDone }: {
  open: boolean;
  onClose: () => void;
  activeGroup?: ShipGroup;
  onDone: () => Promise<void>;
}) {
  const [phone, setPhone] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  type Result = {
    phone: string;
    successes: { deviceNo: string; slotCode: string; orderId: string }[];
    failures: { deviceNo: string; reason: string }[];
  };
  const [result, setResult] = useState<Result | null>(null);

  useEffect(() => {
    if (open) { setPhone(""); setText(""); setBusy(false); setResult(null); }
  }, [open]);
  if (!open) return null;

  const parsedDevices = text
    .split(/[\s,，;；]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const dedupCount = new Set(parsedDevices).size;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeGroup) { toast("请先选择或新建船组", "info"); return; }
    const p = phone.trim();
    if (!/^\d{6,15}$/.test(p)) { toast("接待人员手机号格式不正确（6-15 位数字）", "info"); return; }
    if (parsedDevices.length === 0) { toast("请至少输入一个设备号", "info"); return; }
    setBusy(true);
    try {
      const res = await apiSend<Result>("POST", "/rental/reception-borrow", {
        phone: p,
        deviceNos: parsedDevices,
        shipGroupId: activeGroup.id,
      });
      setResult(res);
      if (res.successes.length > 0) {
        toast(`已借出 ${res.successes.length} 台${res.failures.length > 0 ? `，失败 ${res.failures.length} 台` : ""}`, res.failures.length > 0 ? "info" : "success");
      } else {
        toast("全部设备借出失败，请检查设备号与船组航线", "error");
      }
      await onDone();
    } catch (err) {
      toast(err instanceof Error ? err.message : "接待登记失败", "error");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 p-3 sm:p-4 pt-[max(env(safe-area-inset-top),12px)] pb-[max(env(safe-area-inset-bottom),12px)]" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full max-w-lg p-5 space-y-3 max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-24px)] flex flex-col overscroll-contain" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-gray-900">
          <i className="ri-user-star-line text-amber-600 mr-1.5" />
          接待登记 · 批量借出
        </h3>
        {activeGroup ? (
          <div className="text-xs text-gray-500">
            本组：<b className="text-gray-800">{activeGroup.name}</b>
            {activeGroup.routeName ? ` · ${activeGroup.routeName}` : ""}
            {activeGroup.routeDirection === "up" ? " · 上行" : activeGroup.routeDirection === "down" ? " · 下行" : ""}
          </div>
        ) : (
          <div className="text-xs text-amber-700">未选船组，无法登记</div>
        )}
        {!result ? (
          <form onSubmit={submit} className="space-y-3 overflow-y-auto">
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              接待登记用于无微信支付凭证的场景（VIP / 内部 / 试用）。手机号填的是
              <b>接待人员</b>本人，不是客户。
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">接待人员手机号 *</label>
              <input
                autoFocus
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                placeholder="接待人员手机号，例如 13800138000"
                inputMode="numeric"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
              <p className="text-xs text-gray-400 mt-1">填接待人员（操作员）的手机号，不是客户的。用于对账 / 接待量统计。</p>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">设备号（空格 / 逗号 / 换行分隔） *</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="可粘贴一长串：001 002 003，也可以贴 SX-P-2406-001 SX-P-2406-002 …"
                rows={5}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                已识别 <b className="text-gray-700">{parsedDevices.length}</b> 个
                {dedupCount !== parsedDevices.length && <>（去重后 {dedupCount} 个）</>}
                。无押金，无支付凭证。
              </p>
            </div>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">取消</button>
              <button
                type="submit"
                disabled={busy || !activeGroup || !phone.trim() || parsedDevices.length === 0}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-60"
              >
                {busy ? "登记中…" : `登记借出（${parsedDevices.length}）`}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3 overflow-y-auto">
            <div className="text-sm text-gray-700">
              接待人员 <b className="font-mono">{result.phone}</b> ·
              成功 <b className="text-emerald-600">{result.successes.length}</b> ·
              失败 <b className="text-red-600">{result.failures.length}</b>
            </div>
            {result.successes.length > 0 && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-1.5">
                <div className="text-xs font-semibold text-emerald-700">✓ 已借出</div>
                {result.successes.map((s) => (
                  <div key={s.orderId} className="text-xs text-emerald-900 font-mono">
                    {fmtSlot(s.slotCode) || s.deviceNo} 号 · {s.deviceNo}
                  </div>
                ))}
              </div>
            )}
            {result.failures.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1.5">
                <div className="text-xs font-semibold text-red-700">✗ 失败</div>
                {result.failures.map((f, i) => (
                  <div key={`${f.deviceNo}-${i}`} className="text-xs text-red-900">
                    <span className="font-mono">{f.deviceNo}</span> — {f.reason}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => { setResult(null); setText(""); }}
                className="flex-1 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium hover:bg-amber-100"
              >
                继续登记
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                关闭
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RentalPage;
